/**
 * RetroMusicGenerator v3 - Demoscene-quality procedural music engine.
 *
 * Architecture:
 *   - Markov melody engine with adaptive weighted intervals (smooth, natural)
 *   - Bach-style counterpoint (contrary/oblique motion, consonance rules)
 *   - Chopin-esque modulations (chromatic mediants, secondary dominants, Neapolitan)
 *   - Fractal phrase structure (8>4>2 bar hierarchy, antecedent-consequent)
 *   - Expressive dynamics (crescendo, phrase peaks, metric accents)
 *   - Mixed meters (4/4, 3/4, 6/8) with polyrhythmic overlays (3-against-2)
 *   - Generation-level pre-computation (no material regen at section boundaries)
 *   - Deferred mood transitions (applied at phrase boundaries, not instantly)
 *   - Adaptive rules (tension-driven, not fixed)
 *
 * Instruments: pulse lead, countermelody, chip bass, arpeggiator, synth pad,
 *              pluck, sub bass, wobble synth, retro drums, SFX bleeps.
 *
 * Mobile-hardened: pre-generated noise buffers, 36-voice polyphony cap,
 * tab visibility handling, safe AudioContext resume.
 */
export default class RetroMusicGenerator {
    constructor(config) {
        this.audioContext = null;
        this.masterGain = null;
        this.compressor = null;
        this.reverbNode = null;
        this.delayL = null;
        this.delayR = null;
        this.delayFeedbackL = null;
        this.delayFeedbackR = null;
        this.delayWet = null;
        this.filterMain = null;
        this.bitcrusherShaper = null;
        this._reverbWet = null;
        this._chorusDelayL = null;
        this._chorusDelayR = null;
        this._chorusLfoNode = null;
        this._chorusWet = null;
        this._overdrive = null;
        this._overdriveWet = null;
        this._overdriveGain = null;

        this.isPlaying = false;
        this.config = {
            volume: 0.3, tempo: 120, seed: 1,
            pitchRange: { minSemitone: -24, maxSemitone: 36 },
            ...(config || {})
        };

        this._voices = [];
        this._maxVoices = 36;
        this.oscillators = [];
        this.envelopes = [];

        this.sequencePosition = 0;
        this.nextNoteTime = 0;
        this.lookahead = 25.0;
        this.scheduleAheadTime = 0.3;
        this.timerID = 0;

        this._noiseBufferShort = null;
        this._noiseBufferLong = null;

        this.rng = null;
        this.currentKey = 0;
        this.currentScale = [];
        this.currentScaleName = 'major';
        this.harmonicProgression = [];
        this.currentChordIndex = 0;
        this.currentMood = 'explore';
        this.moodIntensity = 0.5;
        this.targetIntensity = 0.5;
        this.autoMoodMode = false;
        this.nextMoodChange = 0;

        // Pending mood (two-tier: lightweight at bar boundary, heavy at sustained)
        this._pendingMood = null;
        this._pendingMoodSince = 0;  // sequencePosition when pending mood was first set

        this.layerStates = new Map();

        // Generation
        this._generation = 0;
        this._generationLength = 0;
        this._generationStart = 0;
        this._currentScene = null;

        // Meter: stepsPerBar varies (16 for 4/4, 12 for 3/4 or 6/8)
        this._meter = '4/4';
        this._stepsPerBar = 16;
        this._beatsPerBar = 4;

        // Song structure
        this._sectionIndex = 0;
        this._sectionSequence = [];
        this._currentSectionDef = null;

        // Generation-level pre-computed material
        this._genMelody = [];    // [{rhythm:[16], pitches:[{deg,vel}]}] per bar
        this._genBass = [];      // [[{deg,oct,vel}|null x 16]] per bar
        this._genCounter = [];   // [{rhythm:[16], pitches:[{deg,vel}]}] per bar
        this._genChords = [];    // [{root,third,fifth,seventh?}] per bar
        this._genKeys = [];      // [keyOffset] per bar (for modulations)
        this._genScales = [];    // [scaleName|null] per bar (for mode modulations)

        // Melodic motifs (concrete interval sequences for thematic coherence)
        this._motif = [];
        this._motifAnswer = [];
        this._motifCadence = [];
        this._motifDirs = [];

        // Melody state
        this._lastLeadDeg = 0;
        this._melodyOctave = 5;

        // Arp
        this.arpIndex = 0;
        this._arpStyle = 'ascending';

        // Drums
        this._activeDrumKit = null;
        this._currentFill = null;
        this._drumFillBar = -1;
        this._swing = 0;
        this._filterLfoPhase = 0;
        this._reverbEvolvePhase = 0;
        this._overdriveAmount = 0;
        this._prevLeadFreq = 0;

        // Accompaniment mode
        this._accompMode = 'choral'; // choral, arpeggiated, pulsing, sustained

        // Current chord
        this.currentChord = [0, 4, 7];

        // Tempo
        this._baseTempo = 120;
        this.targetTempo = 120;
        this.tempoRampRate = 0.8;

        // Sidechain
        this._duckGain = null;

        // Visibility
        this._visibilityBound = null;

        // ============================================================
        // MUSICAL DATA
        // ============================================================

        this.scales = {
            major:           [0, 2, 4, 5, 7, 9, 11],
            minor:           [0, 2, 3, 5, 7, 8, 10],
            dorian:          [0, 2, 3, 5, 7, 9, 10],
            mixolydian:      [0, 2, 4, 5, 7, 9, 10],
            lydian:          [0, 2, 4, 6, 7, 9, 11],
            phrygian:        [0, 1, 3, 5, 7, 8, 10],
            harmonicMinor:   [0, 2, 3, 5, 7, 8, 11],
            melodicMinor:    [0, 2, 3, 5, 7, 9, 11],
            majorPentatonic: [0, 2, 4, 7, 9],
            minorPentatonic: [0, 3, 5, 7, 10],
            blues:           [0, 3, 5, 6, 7, 10],
            wholeTone:       [0, 2, 4, 6, 8, 10],
            japanese:        [0, 1, 5, 7, 8],
            hungarian:       [0, 2, 3, 6, 7, 8, 11]
        };

        this.moods = {
            explore: {
                intensity: 0.5,
                scenes: [
                    { name: 'wanderer',    baseTempo: 108, swing: 0.06, scales: ['lydian', 'major'],             energy: 0.4, reverbMix: 0.22, delayMix: 0.18, meter: '4/4', polyChance: 0.2 },
                    { name: 'neonDrift',   baseTempo: 116, swing: 0.10, scales: ['dorian', 'mixolydian'],        energy: 0.5, reverbMix: 0.20, delayMix: 0.15, meter: '4/4', polyChance: 0.3 },
                    { name: 'pixelDawn',   baseTempo: 95,  swing: 0.04, scales: ['majorPentatonic', 'japanese'], energy: 0.35, reverbMix: 0.30, delayMix: 0.22, meter: '3/4', polyChance: 0.1 },
                    { name: 'retroGroove', baseTempo: 118, swing: 0.12, scales: ['dorian', 'blues'],            energy: 0.55, reverbMix: 0.16, delayMix: 0.12, meter: '4/4', polyChance: 0.4 }
                ],
                progressions: [
                    [0, 4, 5, 3], [0, 5, 1, 4], [0, 2, 3, 4],
                    [0, 5, 3, 4], [0, 3, 1, 4], [0, 2, 5, 3]
                ],
                sections: ['intro', 'verseA', 'chorus', 'verseB', 'bridge', 'chorus', 'outro'],
                drumKits: ['groove', 'minimal', 'electro'],
                layers: ['lead', 'bass', 'arp', 'pad']
            },
            adventure: {
                intensity: 0.75,
                scenes: [
                    { name: 'questRun',   baseTempo: 130, swing: 0.03, scales: ['mixolydian', 'major'],       energy: 0.7,  reverbMix: 0.15, delayMix: 0.12, meter: '4/4', polyChance: 0.3 },
                    { name: 'skyChase',   baseTempo: 138, swing: 0.0,  scales: ['lydian', 'major'],           energy: 0.8,  reverbMix: 0.18, delayMix: 0.10, meter: '4/4', polyChance: 0.2 },
                    { name: 'jungleDash', baseTempo: 126, swing: 0.08, scales: ['dorian', 'minorPentatonic'], energy: 0.7,  reverbMix: 0.14, delayMix: 0.14, meter: '4/4', polyChance: 0.5 },
                    { name: 'waltzQuest', baseTempo: 140, swing: 0.0,  scales: ['major', 'lydian'],           energy: 0.65, reverbMix: 0.20, delayMix: 0.16, meter: '3/4', polyChance: 0.1 }
                ],
                progressions: [
                    [0, 3, 5, 4], [5, 3, 0, 4], [0, 4, 5, 3],
                    [0, 3, 4, 0], [0, 4, 0, 3], [0, 3, 1, 4]
                ],
                sections: ['intro', 'verseA', 'chorus', 'verseA', 'chorus', 'bridge', 'chorus', 'outro'],
                drumKits: ['driving', 'breakbeat', 'electro'],
                layers: ['lead', 'bass', 'arp', 'drums', 'wobble']
            },
            combat: {
                intensity: 1.0,
                scenes: [
                    { name: 'bossArena', baseTempo: 148, swing: 0.0,  scales: ['harmonicMinor', 'phrygian'],  energy: 1.0,  reverbMix: 0.12, delayMix: 0.08, meter: '4/4', polyChance: 0.4 },
                    { name: 'bitBattle', baseTempo: 152, swing: 0.0,  scales: ['blues', 'dorian'],            energy: 0.95, reverbMix: 0.10, delayMix: 0.06, meter: '4/4', polyChance: 0.3 },
                    { name: 'darkClash', baseTempo: 144, swing: 0.02, scales: ['harmonicMinor', 'hungarian'], energy: 1.0,  reverbMix: 0.14, delayMix: 0.10, meter: '4/4', polyChance: 0.5 },
                    { name: 'oddBattle', baseTempo: 150, swing: 0.0,  scales: ['phrygian', 'harmonicMinor'],  energy: 1.0,  reverbMix: 0.10, delayMix: 0.05, meter: '4/4', polyChance: 0.6 }
                ],
                progressions: [
                    [0, 5, 3, 4], [0, 3, 4, 0], [0, 5, 6, 4],
                    [0, 4, 5, 4], [0, 3, 5, 4], [0, 5, 4, 3]
                ],
                sections: ['intro', 'verseA', 'chorus', 'bridge', 'chorus', 'verseA', 'chorus', 'outro'],
                drumKits: ['intense', 'breakbeat', 'driving'],
                layers: ['lead', 'bass', 'drums', 'wobble', 'sub']
            },
            chill: {
                intensity: 0.3,
                scenes: [
                    { name: 'starGaze',    baseTempo: 84,  swing: 0.08, scales: ['lydian', 'majorPentatonic'],  energy: 0.25, reverbMix: 0.35, delayMix: 0.25, meter: '3/4', polyChance: 0.1 },
                    { name: 'lofiPixel',   baseTempo: 88,  swing: 0.15, scales: ['dorian', 'minorPentatonic'],  energy: 0.3,  reverbMix: 0.30, delayMix: 0.20, meter: '4/4', polyChance: 0.2 },
                    { name: 'dreamDrift',  baseTempo: 78,  swing: 0.05, scales: ['wholeTone', 'lydian'],        energy: 0.2,  reverbMix: 0.40, delayMix: 0.28, meter: '3/4', polyChance: 0.0 },
                    { name: 'retroSunset', baseTempo: 94,  swing: 0.10, scales: ['majorPentatonic', 'major'],   energy: 0.35, reverbMix: 0.28, delayMix: 0.18, meter: '4/4', polyChance: 0.15 }
                ],
                progressions: [
                    [0, 2, 3, 0], [0, 5, 3, 4], [0, 3, 5, 4],
                    [0, 2, 5, 3], [0, 3, 0, 4], [0, 5, 2, 3]
                ],
                sections: ['intro', 'verseA', 'verseB', 'bridge', 'verseA', 'outro'],
                drumKits: ['minimal', 'lofi', 'groove'],
                layers: ['pad', 'lead', 'pluck', 'sub']
            },
            victory: {
                intensity: 1.0,
                scenes: [
                    { name: 'triumph',     baseTempo: 132, swing: 0.0,  scales: ['major', 'majorPentatonic'], energy: 1.0, reverbMix: 0.20, delayMix: 0.15, meter: '4/4', polyChance: 0.2 },
                    { name: 'pixelParade', baseTempo: 126, swing: 0.06, scales: ['lydian', 'major'],          energy: 0.9, reverbMix: 0.22, delayMix: 0.18, meter: '4/4', polyChance: 0.3 }
                ],
                progressions: [
                    [0, 3, 4, 0], [0, 4, 3, 0], [0, 3, 4, 5], [0, 4, 5, 3]
                ],
                sections: ['intro', 'chorus', 'chorus', 'bridge', 'chorus', 'outro'],
                drumKits: ['driving', 'electro', 'breakbeat'],
                layers: ['lead', 'bass', 'arp', 'drums', 'pad', 'pluck', 'sub']
            }
        };

        this._sectionDefs = {
            intro:   { bars: 4, tension: 0.15, energy: 0.3,  leadActive: true,  counterActive: false, arpActive: false, padActive: true,  wobbleActive: false, accomp: 'sustained' },
            verseA:  { bars: 8, tension: 0.35, energy: 0.6,  leadActive: true,  counterActive: false, arpActive: false, padActive: true,  wobbleActive: false, accomp: 'choral' },
            verseB:  { bars: 8, tension: 0.4,  energy: 0.55, leadActive: true,  counterActive: true,  arpActive: true,  padActive: true,  wobbleActive: false, accomp: 'arpeggiated' },
            chorus:  { bars: 8, tension: 0.55, energy: 0.9,  leadActive: true,  counterActive: true,  arpActive: true,  padActive: true,  wobbleActive: true,  accomp: 'pulsing' },
            bridge:  { bars: 4, tension: 0.7,  energy: 0.45, leadActive: true,  counterActive: false, arpActive: false, padActive: true,  wobbleActive: true,  accomp: 'sustained' },
            outro:   { bars: 4, tension: 0.1,  energy: 0.25, leadActive: true,  counterActive: false, arpActive: false, padActive: true,  wobbleActive: false, accomp: 'sustained' }
        };

        // Sentence rhythm templates (4 bars each)
        this._sentenceRhythms = {
            quarters: [
                [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0],
                [1,0,0,0, 1,0,0,0, 1,0,0,0, 0,0,0,0],
                [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0],
                [1,0,0,0, 1,0,0,0, 0,0,0,0, 0,0,0,0]
            ],
            lyrical: [
                [1,0,0,0, 0,0,1,0, 1,0,0,0, 0,0,0,0],
                [1,0,0,0, 1,0,0,0, 0,0,1,0, 0,0,0,0],
                [1,0,0,0, 0,0,1,0, 1,0,0,0, 0,0,0,0],
                [0,0,1,0, 0,0,0,0, 1,0,0,0, 0,0,0,0]
            ],
            syncopated: [
                [0,0,1,0, 0,0,1,0, 1,0,0,0, 0,0,0,0],
                [1,0,0,0, 0,0,1,0, 0,0,1,0, 0,0,0,0],
                [0,0,1,0, 0,0,1,0, 1,0,0,0, 0,0,0,0],
                [1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0]
            ],
            driving: [
                [1,0,0,0, 0,0,1,0, 1,0,0,0, 0,0,0,0],
                [0,0,1,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
                [1,0,0,0, 0,0,1,0, 1,0,0,0, 0,0,0,0],
                [1,0,0,0, 0,0,0,0, 0,0,1,0, 0,0,0,0]
            ],
            sparse: [
                [1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0],
                [0,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0],
                [1,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0],
                [0,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0]
            ],
            energetic: [
                [1,0,0,0, 1,0,1,0, 0,0,0,0, 1,0,0,0],
                [0,0,1,0, 0,0,0,0, 1,0,0,0, 0,0,0,0],
                [1,0,0,0, 1,0,1,0, 0,0,0,0, 1,0,0,0],
                [1,0,0,0, 0,0,1,0, 0,0,0,0, 0,0,0,0]
            ]
        };

        // 3/4 sentence rhythms (12-step bars)
        this._sentenceRhythms3 = {
            waltz: [
                [1,0,0,0, 1,0,0,0, 1,0,0,0],
                [1,0,0,0, 1,0,0,0, 0,0,0,0],
                [1,0,0,0, 1,0,0,0, 1,0,0,0],
                [1,0,0,0, 0,0,0,0, 0,0,0,0]
            ],
            flowing: [
                [1,0,0,0, 0,0,1,0, 0,0,0,0],
                [1,0,0,0, 1,0,0,0, 0,0,0,0],
                [1,0,0,0, 0,0,1,0, 0,0,0,0],
                [0,0,1,0, 0,0,0,0, 1,0,0,0]
            ],
            lively: [
                [1,0,1,0, 1,0,0,0, 1,0,0,0],
                [1,0,0,0, 1,0,1,0, 0,0,0,0],
                [1,0,1,0, 1,0,0,0, 1,0,0,0],
                [1,0,0,0, 1,0,0,0, 0,0,0,0]
            ]
        };

        // Contour types for master shapes
        this._contourTypes = ['arch', 'descent', 'ascent', 'valley', 'plateau', 'wave'];

        // Drum kits (all 16-step for 4/4)
        this._drumKits = {
            minimal: {
                kick:  { hits: [1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0], vel: [1,0,0,0, 0,0,0,0, 0.8,0,0,0, 0,0,0,0] },
                snare: { hits: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0], vel: [0,0,0,0, 0.8,0,0,0, 0,0,0,0, 0.7,0,0,0] },
                hihat: { hits: [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0], vel: [0.7,0,0.3,0, 0.5,0,0.25,0, 0.6,0,0.3,0, 0.5,0,0.25,0] },
                openHat:{ hits: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,1], vel: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0.5] },
                clap: null
            },
            groove: {
                kick:  { hits: [1,0,0,0, 0,0,1,0, 1,0,0,0, 0,0,0,0], vel: [1,0,0,0, 0,0,0.55,0, 0.9,0,0,0, 0,0,0,0] },
                snare: { hits: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,1], vel: [0,0,0,0, 0.9,0,0,0, 0,0,0,0, 0.85,0,0,0.35] },
                hihat: { hits: [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0], vel: [0.8,0,0.3,0, 0.55,0,0.25,0, 0.7,0,0.35,0, 0.55,0,0.3,0] },
                openHat:{ hits: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,1,0,0], vel: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0.45,0,0] },
                clap: null
            },
            driving: {
                kick:  { hits: [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,1,0], vel: [1,0,0,0, 0.8,0,0,0, 0.9,0,0,0, 0.8,0,0.45,0] },
                snare: { hits: [0,0,0,0, 1,0,0,1, 0,0,0,0, 1,0,0,0], vel: [0,0,0,0, 1,0,0,0.35, 0,0,0,0, 0.95,0,0,0] },
                hihat: { hits: [1,0,1,1, 1,0,1,1, 1,0,1,1, 1,0,1,1], vel: [0.75,0,0.35,0.25, 0.65,0,0.3,0.25, 0.75,0,0.35,0.25, 0.65,0,0.3,0.25] },
                openHat:{ hits: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0], vel: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0] },
                clap: { hits: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0], vel: [0,0,0,0, 0.45,0,0,0, 0,0,0,0, 0.45,0,0,0] }
            },
            intense: {
                kick:  { hits: [1,0,1,0, 1,0,0,0, 1,0,1,0, 1,0,0,0], vel: [1,0,0.65,0, 0.9,0,0,0, 1,0,0.6,0, 0.85,0,0,0] },
                snare: { hits: [0,0,0,0, 1,0,0,1, 0,0,0,1, 1,0,0,0], vel: [0,0,0,0, 1,0,0,0.4, 0,0,0,0.35, 0.95,0,0,0] },
                hihat: { hits: [1,1,1,1, 1,1,1,1, 1,1,1,1, 1,1,1,1], vel: [0.7,0.25,0.45,0.25, 0.6,0.2,0.4,0.25, 0.7,0.25,0.45,0.25, 0.6,0.2,0.4,0.25] },
                openHat:{ hits: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,1,0], vel: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0.5,0] },
                clap: { hits: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0], vel: [0,0,0,0, 0.55,0,0,0, 0,0,0,0, 0.55,0,0,0] }
            },
            breakbeat: {
                kick:  { hits: [1,0,0,0, 0,0,0,0, 0,0,1,0, 0,0,0,0], vel: [1,0,0,0, 0,0,0,0, 0,0,0.8,0, 0,0,0,0] },
                snare: { hits: [0,0,0,0, 1,0,0,0, 0,0,0,0, 0,0,1,0], vel: [0,0,0,0, 0.9,0,0,0, 0,0,0,0, 0,0,0.65,0] },
                hihat: { hits: [1,0,1,0, 1,0,0,1, 0,1,1,0, 1,0,1,0], vel: [0.65,0,0.35,0, 0.55,0,0,0.3, 0,0.25,0.45,0, 0.6,0,0.35,0] },
                openHat:{ hits: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,1], vel: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0.45] },
                clap: { hits: [0,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0], vel: [0,0,0,0, 0,0,0,0, 0.45,0,0,0, 0,0,0,0] }
            },
            electro: {
                kick:  { hits: [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0], vel: [1,0,0,0, 0.65,0,0,0, 0.8,0,0,0, 0.65,0,0,0] },
                snare: { hits: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0], vel: [0,0,0,0, 0.85,0,0,0, 0,0,0,0, 0.8,0,0,0] },
                hihat: { hits: [0,0,1,0, 0,0,1,0, 0,0,1,0, 0,0,1,0], vel: [0,0,0.55,0, 0,0,0.45,0, 0,0,0.5,0, 0,0,0.45,0] },
                openHat:{ hits: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0], vel: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0] },
                clap: { hits: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0], vel: [0,0,0,0, 0.6,0,0,0, 0,0,0,0, 0.55,0,0,0] }
            },
            lofi: {
                kick:  { hits: [1,0,0,0, 0,0,0,0, 0,0,1,0, 0,0,0,0], vel: [0.75,0,0,0, 0,0,0,0, 0,0,0.55,0, 0,0,0,0] },
                snare: { hits: [0,0,0,0, 1,0,0,0, 0,0,0,0, 0,0,0,0], vel: [0,0,0,0, 0.6,0,0,0, 0,0,0,0, 0,0,0,0] },
                hihat: { hits: [0,0,1,0, 0,0,0,0, 0,0,1,0, 0,0,0,0], vel: [0,0,0.35,0, 0,0,0,0, 0,0,0.3,0, 0,0,0,0] },
                openHat:{ hits: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0], vel: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0] },
                clap: null
            }
        };

        // 3/4 drum kits (12-step)
        this._drumKits3 = {
            waltz: {
                kick:  { hits: [1,0,0,0, 0,0,0,0, 0,0,0,0], vel: [1,0,0,0, 0,0,0,0, 0,0,0,0] },
                snare: { hits: [0,0,0,0, 1,0,0,0, 1,0,0,0], vel: [0,0,0,0, 0.7,0,0,0, 0.6,0,0,0] },
                hihat: { hits: [1,0,1,0, 1,0,1,0, 1,0,1,0], vel: [0.6,0,0.3,0, 0.5,0,0.25,0, 0.5,0,0.25,0] },
                openHat:{ hits: [0,0,0,0, 0,0,0,0, 0,0,0,0], vel: [0,0,0,0, 0,0,0,0, 0,0,0,0] },
                clap: null
            },
            gentle: {
                kick:  { hits: [1,0,0,0, 0,0,0,0, 0,0,0,0], vel: [0.7,0,0,0, 0,0,0,0, 0,0,0,0] },
                snare: { hits: [0,0,0,0, 0,0,0,0, 1,0,0,0], vel: [0,0,0,0, 0,0,0,0, 0.5,0,0,0] },
                hihat: { hits: [0,0,0,0, 1,0,0,0, 0,0,0,0], vel: [0,0,0,0, 0.4,0,0,0, 0,0,0,0] },
                openHat:{ hits: [0,0,0,0, 0,0,0,0, 0,0,0,0], vel: [0,0,0,0, 0,0,0,0, 0,0,0,0] },
                clap: null
            }
        };

        // Short fills (last 4 steps of bar only)
        this._drumFills = [
            { kick: [0,0,0,0, 0,0,0,0, 0,0,0,0, 1,0,1,1], snare: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,1,0,0], hihat: [0,0,0,0, 0,0,0,0, 0,0,0,0, 1,1,1,1] },
            { kick: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0], snare: [0,0,0,0, 0,0,0,0, 0,0,0,0, 1,0,1,1], hihat: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0] },
            { kick: [0,0,0,0, 0,0,0,0, 0,0,0,0, 1,0,0,0], snare: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,1,1,0], hihat: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,1] },
            { kick: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,1], snare: [0,0,0,0, 0,0,0,0, 0,0,0,0, 1,1,0,0], hihat: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,1,0] }
        ];

        // Arp styles
        this._arpStyles = {
            ascending:  (notes, i) => notes[i % notes.length],
            descending: (notes, i) => notes[(notes.length - 1) - (i % notes.length)],
            pingpong:   (notes, i) => { const c = Math.max(notes.length * 2 - 2, 1); const p = i % c; return p < notes.length ? notes[p] : notes[c - p]; },
            neighbor:   (notes, i) => { const base = notes[i % notes.length]; return (i % 3 === 1) ? base + 1 : (i % 3 === 2) ? base - 1 : base; }
        };

        // Init
        this.setupSeededRandom(this.config.seed || 1);
        this.initializeMusicalSystem();
        this.initializeAudioContext();
    }

    // ============================================================
    // SEEDED RNG
    // ============================================================
    setupSeededRandom(seed) {
        let s = seed || 1;
        this.rng = () => { s = Math.sin(s) * 10000; return s - Math.floor(s); };
    }

    _weightedSelect(weights) {
        const entries = Object.entries(weights);
        let total = 0;
        for (const [, w] of entries) total += Math.max(0, w);
        if (total <= 0) return 0;
        let r = this.rng() * total;
        for (const [k, w] of entries) {
            if (w <= 0) continue;
            r -= w;
            if (r <= 0) return Number(k);
        }
        return 0;
    }

    // ============================================================
    // MUSICAL INITIALIZATION
    // ============================================================
    initializeMusicalSystem() {
        this.currentKey = Math.floor(this.rng() * 12);
        this._initGeneration();
    }

    _initGeneration() {
        this._generation++;
        this._generationStart = this.sequencePosition;

        const moodDef = this.moods[this.currentMood] || this.moods.explore;

        // Scene
        const scenes = moodDef.scenes;
        this._currentScene = scenes[Math.floor(this.rng() * scenes.length)];
        const scene = this._currentScene;

        // Meter
        this._meter = scene.meter || '4/4';
        this._stepsPerBar = this._meter === '3/4' || this._meter === '6/8' ? 12 : 16;
        this._beatsPerBar = this._meter === '3/4' ? 3 : (this._meter === '6/8' ? 6 : 4);

        // Tempo
        this._baseTempo = scene.baseTempo + Math.floor(this.rng() * 10) - 5;
        this.targetTempo = this._baseTempo;
        this.config.tempo = this._baseTempo;
        this._swing = scene.swing;

        // Scale & progression
        const scaleNames = scene.scales;
        this.currentScaleName = scaleNames[Math.floor(this.rng() * scaleNames.length)];
        this.currentScale = [...this.scales[this.currentScaleName]];
        this.harmonicProgression = [...moodDef.progressions[Math.floor(this.rng() * moodDef.progressions.length)]];
        this.currentChordIndex = 0;

        // Drum kit
        if (this._meter === '3/4' || this._meter === '6/8') {
            const kitNames = Object.keys(this._drumKits3);
            this._activeDrumKit = this._drumKits3[kitNames[Math.floor(this.rng() * kitNames.length)]];
        } else {
            const kitNames = moodDef.drumKits;
            this._activeDrumKit = this._drumKits[kitNames[Math.floor(this.rng() * kitNames.length)]] || this._drumKits.groove;
        }

        // Section sequence
        this._sectionSequence = [...(moodDef.sections || ['verseA', 'chorus', 'verseB', 'chorus'])];
        this._sectionIndex = 0;

        // Generation length
        let totalBars = 0;
        for (const sec of this._sectionSequence) totalBars += (this._sectionDefs[sec] || this._sectionDefs.verseA).bars;
        this._generationLength = totalBars * this._stepsPerBar;

        // Generate melodic motifs (concrete hook + variations)
        this._generateMotif();

        // Pre-compute ALL material for the full generation
        this._generateGenerationMaterial(totalBars);

        // Init layers & first section
        this.initializeLayerStates();
        this._applySectionOrchestration(0);
    }

    _generateMotif() {
        // Generate a concrete melodic motif — the "hook" of this generation.
        // A motif is a short sequence of scale-degree intervals that repeats
        // at structural positions to create thematic coherence and catchiness.

        const hookPatterns = [
            // 2-note motifs (very memorable, easiest to recognize)
            { intervals: [2, -1],     weight: 16 },  // up-a-third-step-back (classic hook)
            { intervals: [-2, 1],     weight: 14 },  // drop-recover
            { intervals: [1, -2],     weight: 10 },  // step-up-drop (sighing)
            { intervals: [-1, 2],     weight: 10 },  // step-down-lift
            // 3-note motifs (classic phrase length)
            { intervals: [1, 1, -2],  weight: 14 },  // climb-climb-drop
            { intervals: [-1, -1, 2], weight: 12 },  // descend-descend-recover
            { intervals: [1, -1, 1],  weight: 14 },  // neighbor tone (very singable)
            { intervals: [-1, 1, -1], weight: 12 },  // inverse neighbor
            { intervals: [1, 1, -1],  weight: 12 },  // ascending settle
            { intervals: [0, 1, -1],  weight: 10 },  // hold-step-back (gentle)
            { intervals: [2, -1, -1], weight: 8 },   // third-up then stepwise down
            // 4-note motifs (for longer phrases)
            { intervals: [1, 1, -1, -1], weight: 10 }, // arch (up-up-down-down)
            { intervals: [-1, -1, 1, 1], weight: 8 },  // valley
            { intervals: [1, -1, 1, -1], weight: 8 },  // gentle oscillation
            { intervals: [1, 0, -1, 1],  weight: 7 },  // step-hold-step-step
            { intervals: [0, 1, 1, -2],  weight: 6 },  // hold-climb-drop
        ];

        // Weighted selection
        let total = 0;
        for (const p of hookPatterns) total += p.weight;
        let r = this.rng() * total;
        let chosen = hookPatterns[0];
        for (const p of hookPatterns) {
            r -= p.weight;
            if (r <= 0) { chosen = p; break; }
        }

        const intervals = [...chosen.intervals];

        // Occasionally invert (flip direction) for variety
        if (this.rng() < 0.2) {
            for (let i = 0; i < intervals.length; i++) intervals[i] = -intervals[i];
        }

        // Generate "answer" motif — variation for call-and-response
        const answer = this._createMotifVariation(intervals);

        // Generate "cadential" motif — resolves toward tonic at phrase ends
        const cadence = this._createCadenceMotif();

        this._motif = intervals;
        this._motifAnswer = answer;
        this._motifCadence = cadence;

        // Derive _motifDirs for Markov influence on non-motif bars
        this._motifDirs = [];
        for (const iv of intervals) {
            this._motifDirs.push(iv === 0 ? 0 : Math.sign(iv) * Math.min(2, Math.abs(iv)));
        }
        while (this._motifDirs.length < 4) {
            this._motifDirs.push(this._motifDirs[this._motifDirs.length - 1] || 1);
        }
    }

    _createMotifVariation(intervals) {
        const variation = [...intervals];
        const r = this.rng();
        if (r < 0.3) {
            // Different ending (answer to a question)
            const last = variation[variation.length - 1];
            variation[variation.length - 1] = last > 0 ? -Math.abs(last) : Math.abs(last || 1);
        } else if (r < 0.55) {
            // Inversion (flip all intervals)
            for (let i = 0; i < variation.length; i++) variation[i] = -variation[i];
        } else if (r < 0.75) {
            // Modify one interior interval
            if (variation.length >= 3) {
                const idx = 1 + Math.floor(this.rng() * (variation.length - 2));
                variation[idx] += (this.rng() < 0.5 ? 1 : -1);
            } else {
                variation[0] += (this.rng() < 0.5 ? 1 : -1);
            }
        } else {
            // Retrograde (reverse and negate)
            variation.reverse();
            for (let i = 0; i < variation.length; i++) variation[i] = -variation[i];
        }
        return variation;
    }

    _createCadenceMotif() {
        const patterns = [
            [1, -1],    // neighbor then resolve
            [-1, -1],   // step down to tonic
            [1, -2],    // up then resolve down
            [-1, 0],    // step down then hold
            [-2, 1],    // drop then half-resolve
        ];
        return patterns[Math.floor(this.rng() * patterns.length)];
    }

    _nearestChordTone(currentDeg, chordDegs, scaleLen) {
        let best = currentDeg;
        let bestDist = Infinity;
        for (const ct of chordDegs) {
            for (let octOffset = -2; octOffset <= 2; octOffset++) {
                const candidate = ct + octOffset * scaleLen;
                const dist = Math.abs(candidate - currentDeg);
                if (dist < bestDist) { bestDist = dist; best = candidate; }
            }
        }
        return best;
    }

    // ============================================================
    // GENERATION-LEVEL MATERIAL PRE-COMPUTATION
    // ============================================================

    _generateGenerationMaterial(totalBars) {
        this._genMelody = [];
        this._genBass = [];
        this._genCounter = [];
        this._genChords = [];
        this._genKeys = [];
        this._genScales = [];

        // Master contour for the entire generation
        const masterContour = this._makeContour(
            this._contourTypes[Math.floor(this.rng() * this._contourTypes.length)],
            totalBars
        );

        // Pick sentence rhythm template
        const rhythmTemplates = this._meter === '3/4' || this._meter === '6/8' ? this._sentenceRhythms3 : this._sentenceRhythms;
        const rhythmNames = Object.keys(rhythmTemplates);

        // Pick two rhythm types for A and B sentences
        const rhythmA = rhythmTemplates[rhythmNames[Math.floor(this.rng() * rhythmNames.length)]];
        const rhythmB = rhythmTemplates[rhythmNames[Math.floor(this.rng() * rhythmNames.length)]];

        let prevLeadDeg = 0;
        let prevLeadInterval = 0;
        let prevBassDeg = 0;
        let currentKeyOffset = 0;
        let currentScaleOverride = null; // null = use generation base scale
        const spb = this._stepsPerBar;

        // Track section boundaries for modulation
        let barsSoFar = 0;
        let secIdx = 0;
        let barInSection = 0;
        let currentSecName = this._sectionSequence[0] || 'verseA';
        let currentSecDef = this._sectionDefs[currentSecName] || this._sectionDefs.verseA;
        let tonicizationBarsLeft = 0; // countdown for temporary key shifts
        let savedKeyBeforeTonicization = 0;

        for (let bar = 0; bar < totalBars; bar++) {
            // Advance section tracking
            if (barInSection >= currentSecDef.bars && secIdx + 1 < this._sectionSequence.length) {
                secIdx++;
                currentSecName = this._sectionSequence[secIdx] || 'verseA';
                currentSecDef = this._sectionDefs[currentSecName] || this._sectionDefs.verseA;
                barInSection = 0;
                tonicizationBarsLeft = 0; // reset tonicization at section boundary

                // Bridge modulation: full key change
                if (currentSecName === 'bridge') {
                    currentKeyOffset = this._pickModulation();
                    // Mode shift on bridge (e.g., to relative minor or parallel mode)
                    currentScaleOverride = this._pickModeShift();
                } else if (secIdx > 0 && this._sectionSequence[secIdx - 1] === 'bridge') {
                    currentKeyOffset = 0; // return from bridge
                    currentScaleOverride = null; // return to base scale
                }

                // Chorus: occasional mode brightening
                if (currentSecName === 'chorus' && this.rng() < 0.3) {
                    currentScaleOverride = this._pickBrighterMode();
                } else if (currentSecName !== 'bridge') {
                    if (this.rng() < 0.15) currentScaleOverride = null; // occasionally reset
                }
            }

            // Mid-section tonicization at 4-bar phrase boundaries
            if (barInSection > 0 && barInSection % 4 === 0 && currentSecDef.bars >= 8) {
                if (tonicizationBarsLeft > 0) {
                    tonicizationBarsLeft--;
                    if (tonicizationBarsLeft === 0) {
                        // Return from tonicization
                        currentKeyOffset = savedKeyBeforeTonicization;
                    }
                } else if (this.rng() < currentSecDef.tension * 0.4) {
                    // Start tonicization: temporary key shift for 2 bars
                    savedKeyBeforeTonicization = currentKeyOffset;
                    const tonicShift = this._pickTonicization();
                    currentKeyOffset = (currentKeyOffset + tonicShift + 12) % 12;
                    tonicizationBarsLeft = 2;
                }
            }

            // Tick tonicization countdown on non-phrase-boundary bars
            if (tonicizationBarsLeft > 0 && barInSection % 4 !== 0) {
                tonicizationBarsLeft--;
                if (tonicizationBarsLeft === 0) {
                    currentKeyOffset = savedKeyBeforeTonicization;
                }
            }

            this._genKeys.push(currentKeyOffset);
            this._genScales.push(currentScaleOverride);

            // Chord for this bar (with possible harmonic color)
            const progIdx = bar % this.harmonicProgression.length;
            const chordDegs = this._buildChord(this.harmonicProgression[progIdx], currentSecDef.tension, bar, totalBars);
            this._genChords.push(chordDegs);

            // Determine which sentence we're in (4-bar grouping)
            const sentenceIdx = Math.floor(barInSection / 4);
            const barInSentence = barInSection % 4;
            const isASentence = sentenceIdx % 2 === 0;
            const rhythmSet = isASentence ? rhythmA : rhythmB;
            const rhythm = rhythmSet ? [...(rhythmSet[barInSentence % rhythmSet.length] || rhythmSet[0])] : new Array(spb).fill(0);

            // Rhythm density scaling based on section
            if (currentSecDef.energy < 0.35) {
                // Thin out rhythm in low energy sections
                for (let i = 0; i < rhythm.length; i++) {
                    if (rhythm[i] && this.rng() > currentSecDef.energy * 2.5) rhythm[i] = 0;
                }
            }

            // Contour target for this bar
            const contourTarget = masterContour[bar] || 0.5;
            // Chorus: shift contour up
            if (currentSecName === 'chorus') {
                prevLeadDeg = Math.max(prevLeadDeg, prevLeadDeg + (this.rng() < 0.3 ? 1 : 0));
            }

            // Count note onsets
            const noteCount = rhythm.reduce((s, v) => s + v, 0);

            // --- Motif-based melody composition ---
            // Select motif based on phrase position for thematic coherence:
            //   Bar 0: state the hook motif (recognition)
            //   Bar 1: free development (contrast)
            //   Bar 2: answer motif in chorus, sometimes in other sections (call-and-response)
            //   Bar 3: cadence motif at phrase end (resolution)
            const isMotifBar = barInSentence === 0;
            const isAnswerBar = barInSentence === 2 && (currentSecName === 'chorus' || this.rng() < 0.35);
            const isCadenceBar = barInSentence === 3;
            const activeMotif = isMotifBar ? this._motif :
                                isAnswerBar ? this._motifAnswer :
                                (isCadenceBar && this.rng() < 0.5) ? this._motifCadence : null;

            // On motif bars, anchor starting note to nearest chord tone
            if (activeMotif && noteCount > 0) {
                const nearest = this._nearestChordTone(prevLeadDeg, chordDegs, this.currentScale.length);
                if (Math.abs(nearest - prevLeadDeg) <= 2) prevLeadDeg = nearest;
            }

            // Generate melody bar
            const melodyPitches = [];
            const tension = currentSecDef.tension;
            let noteInBar = 0;
            let motifNoteIdx = 0;

            for (let step = 0; step < spb; step++) {
                if (!rhythm[step]) continue;

                const isStrongBeat = (step % 4 === 0);
                const isCadencePoint = (barInSentence === 3 && step >= spb - 4);
                let deg, interval;

                if (activeMotif && motifNoteIdx < activeMotif.length) {
                    // Use motif interval directly for thematic coherence
                    interval = activeMotif[motifNoteIdx];
                    deg = prevLeadDeg + interval;
                    motifNoteIdx++;
                } else {
                    // Free Markov generation (fills rest of bar with developed material)
                    const motifDir = this._motifDirs[noteInBar % this._motifDirs.length];
                    const result = this._markovStep(
                        prevLeadDeg, prevLeadInterval, contourTarget, chordDegs,
                        isStrongBeat, tension, motifDir, isCadencePoint
                    );
                    deg = result.deg;
                    interval = result.interval;
                }

                // Expressive dynamics
                const vel = this._expressiveVelocity(deg, prevLeadDeg, prevLeadInterval, step, noteInBar, noteCount, spb);

                melodyPitches.push({ deg, vel });
                prevLeadInterval = deg - prevLeadDeg;
                prevLeadDeg = deg;
                noteInBar++;
            }

            this._genMelody.push({ rhythm, pitches: melodyPitches });

            // Generate bass bar
            const bassBar = this._generateBassBar(chordDegs, currentSecDef.energy, prevBassDeg, bar, totalBars);
            this._genBass.push(bassBar);
            // Track last bass degree
            for (let i = spb - 1; i >= 0; i--) {
                if (bassBar[i]) { prevBassDeg = bassBar[i].deg; break; }
            }

            // Generate counterpoint bar (Bach-style)
            const counterBar = this._generateCounterBar(this._genMelody[bar], chordDegs, currentSecDef, barInSentence);
            this._genCounter.push(counterBar);

            barInSection++;
        }

        this._lastLeadDeg = prevLeadDeg;
    }

    // ============================================================
    // MARKOV MELODY ENGINE
    // ============================================================

    _markovStep(prevDeg, prevInterval, contourTarget, chordDegs, isStrongBeat, tension, motifDir, isCadence) {
        // Base weights: strongly favor stepwise motion (conjunct, singable melody)
        const w = { '-4': 1, '-3': 2, '-2': 8, '-1': 25, '0': 7, '1': 25, '2': 8, '3': 2, '4': 1 };

        // Tension scaling: higher tension loosens intervals slightly (not dramatically)
        const tensionMul = 0.7 + tension * 0.8;  // range 0.7-1.5 (was 0.5-2.0)
        w['-3'] *= tensionMul; w['3'] *= tensionMul;
        w['-4'] *= tensionMul; w['4'] *= tensionMul;

        // Motif directional bias (thematic coherence on free bars)
        if (motifDir > 0) { w['1'] += 10; w['2'] += 4; }
        else if (motifDir < 0) { w['-1'] += 10; w['-2'] += 4; }
        else if (motifDir === 0) { w['0'] += 8; }
        if (Math.abs(motifDir) >= 2) {
            // Boost the third (skip) not the fourth/fifth (leap)
            const d = Math.sign(motifDir);
            w[String(d * 2)] += 5;
        }

        // Contour guidance (gentle macro-level shape)
        const targetCenter = (contourTarget - 0.5) * 12;
        const drift = targetCenter - prevDeg;
        if (Math.abs(drift) > 2) {
            const dir = Math.sign(drift);
            w[String(dir)] += 6;
            w[String(dir * 2)] += 3;
        }

        // Register gravity: pull back when too far from center
        if (Math.abs(prevDeg) > 6) {
            const pullDir = -Math.sign(prevDeg);
            w[String(pullDir)] += Math.abs(prevDeg) - 4;
            w[String(pullDir * 2)] += Math.max(0, Math.abs(prevDeg) - 6);
        }

        // Leap resolution (Fux counterpoint rule): after a leap, resolve by step in opposite direction
        if (Math.abs(prevInterval) >= 3) {
            const resolveDir = -Math.sign(prevInterval);
            const resolveStrength = 25 + (1 - tension) * 20;
            w[String(resolveDir)] += resolveStrength;
            w[String(resolveDir * 2)] += resolveStrength * 0.25;
            // Penalize continuing in leap direction (avoid aimless leaping)
            const leapDir = String(Math.sign(prevInterval));
            w[leapDir] = Math.max(1, (w[leapDir] || 1) - 10);
        }

        // After unison, prefer movement
        if (prevInterval === 0) { w['1'] += 8; w['-1'] += 8; w['0'] = Math.max(1, w['0'] - 5); }

        // --- Tendency tone resolution (tonal gravity) ---
        const scaleLen = this.currentScale.length;
        if (scaleLen >= 5) {
            const degInScale = ((prevDeg % scaleLen) + scaleLen) % scaleLen;
            // Leading tone → tonic (strongest pull in tonal music)
            if (degInScale === scaleLen - 1) {
                w['1'] += 14;
                w['-1'] = Math.max(1, (w['-1'] || 1) - 5);
            }
            // Supertonic → tonic at cadence points
            if (degInScale === 1 && isCadence) {
                w['-1'] += 10;
            }
            // Fourth scale degree → third (fa → mi resolution)
            if (scaleLen >= 7 && degInScale === 3) {
                w['-1'] += 6;
            }
        }

        // Chord-tone gravity — on ALL beats, stronger on strong beats
        // This creates a tighter melody-harmony relationship
        const chordMul = isStrongBeat ? 1.8 : 1.3;
        for (const k of Object.keys(w)) {
            const candidate = prevDeg + Number(k);
            if (this._isChordTone(candidate, chordDegs)) {
                w[k] = (w[k] || 0) * chordMul;
            }
        }

        // Cadence: tonic approach with classic resolution patterns
        if (isCadence) {
            const normDeg = ((prevDeg % scaleLen) + scaleLen) % scaleLen;
            if (normDeg === 1) {
                // Scale degree 2 → tonic: step down
                w['-1'] += 22;
            } else if (normDeg === scaleLen - 1) {
                // Leading tone → tonic: step up
                w['1'] += 22;
            } else {
                // General tonic approach
                const tonicDist = 0 - prevDeg;
                if (Math.abs(tonicDist) <= 4) {
                    const dir = Math.sign(tonicDist) || 1;
                    w[String(dir)] += 18;
                    if (Math.abs(tonicDist) <= 2) w[String(dir)] += 8;
                }
            }
        }

        const interval = this._weightedSelect(w);
        return { deg: prevDeg + interval, interval };
    }

    _isChordTone(deg, chordDegs) {
        const scaleLen = this.currentScale.length;
        const normDeg = ((deg % scaleLen) + scaleLen) % scaleLen;
        return chordDegs.includes(normDeg);
    }

    // ============================================================
    // EXPRESSIVE DYNAMICS
    // ============================================================

    _expressiveVelocity(deg, prevDeg, prevInterval, step, noteInPhrase, totalNotes, stepsPerBar) {
        let vel = 0.7; // base

        // Metric hierarchy
        if (step === 0) vel += 0.08;                    // beat 1 accent
        else if (step % (stepsPerBar / 2) === 0) vel += 0.04;  // mid-bar
        else if (step % 4 === 0) vel += 0.02;           // other beats

        // Ascending = crescendo, descending = diminuendo
        if (prevInterval > 0) vel += 0.04;
        else if (prevInterval < 0) vel -= 0.03;

        // Phrase arc: build to middle, soften at end
        if (totalNotes > 2) {
            const phraseFrac = noteInPhrase / (totalNotes - 1);
            const arc = Math.sin(Math.PI * phraseFrac); // peaks at middle
            vel += (arc - 0.5) * 0.12;
        }

        // Phrase start: gentle onset
        if (noteInPhrase === 0) vel -= 0.05;

        // Repeated note: soften
        if (deg === prevDeg) vel -= 0.06;

        return Math.max(0.3, Math.min(1.0, vel));
    }

    // ============================================================
    // CHORD SYSTEM (with Chopin-esque color)
    // ============================================================

    _buildChord(progDeg, tension, barIdx, totalBars) {
        const scale = this.currentScale;
        const len = scale.length;
        const root = progDeg % len;
        const third = (progDeg + 2) % len;
        const fifth = (progDeg + 4) % len;
        const degs = [root, third, fifth];

        // 7th chords: adds warmth and richness, more likely at higher tension
        if (this.rng() < tension * 0.5 + 0.1) {
            degs.push((progDeg + 6) % len);
        }

        // 9th on chords with 7ths at high tension (lush, jazzy color)
        if (tension > 0.5 && degs.length >= 4 && this.rng() < tension * 0.15) {
            degs.push((progDeg + 1) % len);
        }

        // Suspended chords (sus4/sus2): creates beautiful tension-release
        // Avoid suspending at cadence points (bar 3 of phrase) where resolution matters
        if (this.rng() < tension * 0.12 && barIdx % 4 !== 3) {
            if (this.rng() < 0.6) {
                degs[1] = (progDeg + 3) % len; // sus4
            } else {
                degs[1] = (progDeg + 1) % len; // sus2
            }
        }

        return degs;
    }

    _pickModulation() {
        // Chromatic mediant or closely related key for bridge modulation
        const choices = [
            3, 4, 5, 7, 8, 9, // minor 3rd, major 3rd, 4th, 5th, minor 6th, major 6th
            -3, -5            // down minor 3rd, down 4th
        ];
        return choices[Math.floor(this.rng() * choices.length)];
    }

    _pickTonicization() {
        // Temporary key shift for mid-section color: secondary dominant targets
        const r = this.rng();
        if (r < 0.3) return 7;   // tonicize V (up a 5th)
        if (r < 0.5) return 5;   // tonicize IV (up a 4th)
        if (r < 0.65) return 9;  // tonicize vi (up a major 6th)
        if (r < 0.8) return 2;   // tonicize ii (up a whole step)
        if (r < 0.9) return 4;   // tonicize III (chromatic mediant)
        return -2;               // tonicize bVII (down a whole step)
    }

    _pickModeShift() {
        // Pick a contrasting scale for modulation sections
        const r = this.rng();
        const isMinorBase = this.currentScaleName === 'minor' || this.currentScaleName === 'dorian' ||
                           this.currentScaleName === 'phrygian' || this.currentScaleName === 'harmonicMinor';
        if (isMinorBase) {
            // Minor → something brighter
            if (r < 0.3) return 'dorian';
            if (r < 0.5) return 'mixolydian';
            if (r < 0.7) return 'melodicMinor';
            if (r < 0.85) return 'harmonicMinor';
            return 'lydian';
        } else {
            // Major → something darker or more exotic
            if (r < 0.3) return 'dorian';
            if (r < 0.5) return 'mixolydian';
            if (r < 0.65) return 'harmonicMinor';
            if (r < 0.8) return 'phrygian';
            if (r < 0.9) return 'hungarian';
            return 'melodicMinor';
        }
    }

    _pickBrighterMode() {
        // Brighten the mode for chorus lift
        const r = this.rng();
        if (r < 0.35) return 'lydian';
        if (r < 0.6) return 'major';
        if (r < 0.8) return 'mixolydian';
        return null; // keep current
    }

    updateChord() {
        const progDeg = this.harmonicProgression[this.currentChordIndex % this.harmonicProgression.length];
        const scale = this.currentScale;
        const len = scale.length;
        this.currentChord = [
            scale[progDeg % len],
            scale[(progDeg + 2) % len],
            scale[(progDeg + 4) % len]
        ];
        if (this.rng() < 0.3) this.currentChord.push(scale[(progDeg + 6) % len]);
    }

    // ============================================================
    // BASS ENGINE (Walking / Pedal / Octave)
    // ============================================================

    _generateBassBar(chordDegs, energy, prevDeg, barIdx, totalBars) {
        const spb = this._stepsPerBar;
        const bar = new Array(spb).fill(null);
        const root = chordDegs[0];
        const fifth = chordDegs.length > 2 ? chordDegs[2] : root;

        if (energy > 0.7) {
            // Walking bass: stepwise motion
            bar[0] = { deg: root, oct: 2, vel: 0.9 };
            let cur = root;
            const targets = [root + 2, fifth, root + 2, root]; // passing tone targets
            for (let i = 1; i < 4; i++) {
                const beatPos = i * (spb / 4);
                if (beatPos < spb) {
                    const target = targets[i % targets.length];
                    // Step toward target
                    const step = Math.sign(target - cur) || 1;
                    cur = cur + step;
                    bar[beatPos] = { deg: cur, oct: 2, vel: 0.6 + i * 0.05 };
                }
            }
            // Approach note for next bar
            const lastBeatPos = spb - (spb / 4);
            if (lastBeatPos > 0 && lastBeatPos < spb) {
                bar[lastBeatPos] = { deg: cur, oct: 2, vel: 0.55 };
            }
        } else if (energy > 0.35) {
            // Half-note bass
            bar[0] = { deg: root, oct: 2, vel: 0.8 };
            bar[Math.floor(spb / 2)] = { deg: fifth, oct: 2, vel: 0.6 };
        } else {
            // Pedal bass: just root on beat 1
            bar[0] = { deg: root, oct: 2, vel: 0.65 };
        }

        return bar;
    }

    // ============================================================
    // BACH-STYLE COUNTERPOINT
    // ============================================================

    _generateCounterBar(leadBar, chordDegs, secDef, barInSentence) {
        const spb = this._stepsPerBar;
        // Counter uses sparser rhythm (every other bar, or on even beats only)
        const rhythm = new Array(spb).fill(0);
        const pitches = [];

        if (!secDef.counterActive) return { rhythm, pitches };

        // Counter plays on different beats than lead
        const leadOnsets = [];
        for (let i = 0; i < spb; i++) if (leadBar.rhythm[i]) leadOnsets.push(i);

        // Place counter notes on off-beats relative to lead
        const counterPositions = [];
        for (let i = 0; i < spb; i += 4) {
            // Check if lead has an onset near here
            const nearLead = leadOnsets.some(o => Math.abs(o - i) <= 1);
            if (!nearLead && this.rng() < 0.5) {
                counterPositions.push(i);
            } else if (i + 2 < spb && this.rng() < 0.4) {
                counterPositions.push(i + 2); // offset by 8th note
            }
        }

        if (counterPositions.length === 0) return { rhythm, pitches };

        let prevCounterDeg = null;
        let noteIdx = 0;

        for (const pos of counterPositions) {
            rhythm[pos] = 1;

            // Find the nearest lead note to determine motion type
            const nearestLeadIdx = this._findNearestOnset(leadBar, pos);
            const leadDeg = nearestLeadIdx >= 0 && nearestLeadIdx < leadBar.pitches.length
                ? leadBar.pitches[nearestLeadIdx].deg : 0;

            let counterDeg;

            if (prevCounterDeg === null) {
                // First note: place a consonant interval (3rd or 6th above/below)
                counterDeg = leadDeg + (this.rng() < 0.6 ? 2 : 5); // 3rd or 6th above
            } else {
                // Determine motion type
                const leadMotion = leadBar.pitches.length > 1
                    ? leadBar.pitches[Math.min(noteIdx, leadBar.pitches.length - 1)].deg - leadDeg
                    : 0;
                const r = this.rng();

                if (r < 0.55) {
                    // Contrary motion (Bach's favorite)
                    const contraryStep = -Math.sign(leadMotion || 1) * (1 + Math.floor(this.rng() * 2));
                    counterDeg = prevCounterDeg + contraryStep;
                } else if (r < 0.75) {
                    // Oblique motion (one voice holds)
                    counterDeg = prevCounterDeg;
                } else if (r < 0.9) {
                    // Similar motion (same dir, different interval)
                    counterDeg = prevCounterDeg + Math.sign(leadMotion || 1) * (1 + Math.floor(this.rng() * 2));
                } else {
                    // Parallel 3rds/6ths (acceptable)
                    counterDeg = leadDeg + (this.rng() < 0.5 ? 2 : 5);
                }

                // Consonance check on strong beats: ensure interval is 3rd, 5th, 6th, or octave
                if (pos % 4 === 0) {
                    const interval = Math.abs(counterDeg - leadDeg) % 7;
                    if (interval === 0 || interval === 1 || interval === 6) {
                        // Unison, 2nd, or 7th on strong beat - adjust to nearest consonance
                        counterDeg += (this.rng() < 0.5 ? 1 : -1);
                    }
                }
            }

            const vel = 0.5 + (pos === 0 ? 0.1 : 0) + this.rng() * 0.1;
            pitches.push({ deg: counterDeg, vel: Math.min(0.8, vel) });
            prevCounterDeg = counterDeg;
            noteIdx++;
        }

        return { rhythm, pitches };
    }

    _findNearestOnset(bar, pos) {
        let nearestIdx = -1;
        let nearestDist = Infinity;
        let idx = 0;
        for (let i = 0; i < bar.rhythm.length; i++) {
            if (bar.rhythm[i]) {
                const dist = Math.abs(i - pos);
                if (dist < nearestDist) { nearestDist = dist; nearestIdx = idx; }
                idx++;
            }
        }
        return nearestIdx;
    }

    // ============================================================
    // CONTOUR GENERATOR
    // ============================================================

    _makeContour(type, length) {
        if (length <= 0) return [];
        if (length === 1) return [0.5];
        const c = new Array(length);
        const L = length - 1;
        switch (type) {
            case 'arch':    for (let i = 0; i < length; i++) c[i] = Math.sin(Math.PI * i / L); break;
            case 'descent': for (let i = 0; i < length; i++) c[i] = 1 - i / L; break;
            case 'ascent':  for (let i = 0; i < length; i++) c[i] = i / L; break;
            case 'valley':  for (let i = 0; i < length; i++) c[i] = 1 - Math.sin(Math.PI * i / L); break;
            case 'plateau': for (let i = 0; i < length; i++) c[i] = Math.min(1, (i / L) * 2); break;
            case 'wave':    for (let i = 0; i < length; i++) c[i] = 0.5 + 0.5 * Math.sin(2 * Math.PI * i / L); break;
            default:        for (let i = 0; i < length; i++) c[i] = 0.5;
        }
        return c;
    }

    // ============================================================
    // SECTION ORCHESTRATION (no material regen!)
    // ============================================================

    _applySectionOrchestration(secIdx) {
        const secName = this._sectionSequence[secIdx] || 'verseA';
        this._currentSectionDef = { ...(this._sectionDefs[secName] || this._sectionDefs.verseA) };
        const secDef = this._currentSectionDef;

        // Pick fill once for this section
        this._currentFill = this._drumFills[Math.floor(this.rng() * this._drumFills.length)];
        this._drumFillBar = secDef.bars - 1;

        // Accompaniment mode
        this._accompMode = secDef.accomp || 'choral';

        // Arp style
        const arpNames = Object.keys(this._arpStyles);
        this._arpStyle = arpNames[Math.floor(this.rng() * arpNames.length)];
        this.arpIndex = 0;

        // Tempo nudge
        if (secName === 'chorus') this.targetTempo = this._baseTempo + 2 + Math.floor(this.rng() * 3);
        else if (secName === 'bridge') this.targetTempo = this._baseTempo - 3 + Math.floor(this.rng() * 2);
        else if (secName === 'intro' || secName === 'outro') this.targetTempo = this._baseTempo - 1;
        else this.targetTempo = this._baseTempo + Math.floor(this.rng() * 3) - 1;

        // Update layer targets (smooth crossfade toward new levels)
        const moodDef = this.moods[this.currentMood] || this.moods.explore;
        const moodLayers = moodDef.layers;
        const allLayers = ['lead', 'bass', 'arp', 'pad', 'drums', 'pluck', 'sub', 'wobble', 'counter'];

        for (const layer of allLayers) {
            const st = this.layerStates.get(layer);
            if (!st) continue;
            let target = 0;
            if (moodLayers.includes(layer) || layer === 'drums') {
                target = secDef.energy * (0.5 + this.rng() * 0.4);
            }
            if (layer === 'lead')    target *= secDef.leadActive ? 1.0 : 0.0;
            if (layer === 'counter') target *= secDef.counterActive ? 0.55 : 0.0;
            if (layer === 'arp')     target *= secDef.arpActive ? 0.8 : 0.0;
            if (layer === 'pad')     target *= secDef.padActive ? 0.7 : 0.0;
            if (layer === 'wobble')  target *= secDef.wobbleActive ? 0.6 : 0.0;
            if (layer === 'drums')   target *= secDef.energy;
            if (layer === 'pluck')   target *= (secName === 'verseB' || secName === 'chorus') ? 0.5 : 0.15;
            st.targetIntensity = Math.max(0, Math.min(1, target));
        }
    }

    initializeLayerStates() {
        const allLayers = ['lead', 'bass', 'arp', 'pad', 'drums', 'pluck', 'sub', 'wobble', 'counter'];
        this.layerStates.clear();
        for (const layer of allLayers) {
            this.layerStates.set(layer, { active: false, intensity: 0.0, targetIntensity: 0.0 });
        }
    }

    // ============================================================
    // AUDIO GRAPH (unchanged from v2)
    // ============================================================

    initializeAudioContext() {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.masterGain = this.audioContext.createGain();
        this.masterGain.gain.value = this.config.volume != null ? this.config.volume : 0.3;
        this._generateNoiseBuffers();
        this.setupEffectsChain();
        this.masterGain.connect(this.audioContext.destination);
        this._bindVisibilityHandler();
    }

    _generateNoiseBuffers() {
        const ac = this.audioContext; const sr = ac.sampleRate;
        const shortLen = Math.ceil(sr * 0.05);
        this._noiseBufferShort = ac.createBuffer(1, shortLen, sr);
        const sd = this._noiseBufferShort.getChannelData(0);
        for (let i = 0; i < shortLen; i++) sd[i] = Math.random() * 2 - 1;
        const longLen = Math.ceil(sr * 0.2);
        this._noiseBufferLong = ac.createBuffer(1, longLen, sr);
        const ld = this._noiseBufferLong.getChannelData(0);
        for (let i = 0; i < longLen; i++) ld[i] = Math.random() * 2 - 1;
    }

    _bindVisibilityHandler() {
        this._visibilityBound = () => {
            if (!this.audioContext) return;
            if (document.hidden) { if (this.timerID) { clearTimeout(this.timerID); this.timerID = 0; } }
            else if (this.isPlaying) {
                if (this.audioContext.state === 'suspended') this.audioContext.resume().catch(() => {});
                this.nextNoteTime = this.audioContext.currentTime + 0.1;
                if (!this.timerID) this.scheduler();
            }
        };
        document.addEventListener('visibilitychange', this._visibilityBound);
    }

    setupEffectsChain() {
        const ac = this.audioContext;
        const scene = this._currentScene || { reverbMix: 0.18, delayMix: 0.15 };

        // Compressor
        this.compressor = ac.createDynamicsCompressor();
        this.compressor.threshold.value = -18; this.compressor.knee.value = 10;
        this.compressor.ratio.value = 4; this.compressor.attack.value = 0.003; this.compressor.release.value = 0.15;

        // Main filter (with resonance for techno sweeps)
        this.filterMain = ac.createBiquadFilter();
        this.filterMain.type = 'lowpass'; this.filterMain.frequency.value = 8000; this.filterMain.Q.value = 0.7;

        // Bitcrusher (retro character)
        this.bitcrusherShaper = ac.createWaveShaper();
        this.bitcrusherShaper.curve = this._makeBitcrushCurve(12); this.bitcrusherShaper.oversample = '2x';

        // Overdrive/distortion (tunable waveshaper)
        this._overdrive = ac.createWaveShaper();
        this._overdrive.curve = this._makeOverdriveCurve(1.0); this._overdrive.oversample = '4x';
        this._overdriveWet = ac.createGain(); this._overdriveWet.gain.value = 0;
        this._overdriveGain = ac.createGain(); this._overdriveGain.gain.value = 0.4;

        // Reverb
        this.reverbNode = ac.createConvolver(); this._createPlateReverb();
        const reverbWet = ac.createGain(); reverbWet.gain.value = scene.reverbMix; this._reverbWet = reverbWet;
        const reverbDry = ac.createGain(); reverbDry.gain.value = 0.85;

        // Stereo delay (tempo-synced)
        this.delayL = ac.createDelay(2.0); this.delayR = ac.createDelay(2.0);
        const bl = 60 / (this.config.tempo || 120);
        this.delayL.delayTime.value = bl * 0.75; this.delayR.delayTime.value = bl * 0.5;
        this.delayFeedbackL = ac.createGain(); this.delayFeedbackL.gain.value = 0.25;
        this.delayFeedbackR = ac.createGain(); this.delayFeedbackR.gain.value = 0.25;
        this.delayWet = ac.createGain(); this.delayWet.gain.value = scene.delayMix;

        // Delay cross-feedback
        this.delayL.connect(this.delayFeedbackL); this.delayFeedbackL.connect(this.delayR);
        this.delayR.connect(this.delayFeedbackR); this.delayFeedbackR.connect(this.delayL);
        this.delayL.connect(this.delayWet); this.delayR.connect(this.delayWet);

        // Chorus (classic detuned delay lines with LFO)
        this._chorusDelayL = ac.createDelay(0.05);
        this._chorusDelayR = ac.createDelay(0.05);
        this._chorusDelayL.delayTime.value = 0.005; // 5ms base
        this._chorusDelayR.delayTime.value = 0.007; // 7ms base (asymmetric for stereo width)
        this._chorusWet = ac.createGain(); this._chorusWet.gain.value = 0.18;

        // Chorus LFO (modulates delay time for detuning)
        this._chorusLfoNode = ac.createOscillator(); this._chorusLfoNode.type = 'sine'; this._chorusLfoNode.frequency.value = 0.8;
        const chorusLfoGainL = ac.createGain(); chorusLfoGainL.gain.value = 0.002; // ±2ms modulation
        const chorusLfoGainR = ac.createGain(); chorusLfoGainR.gain.value = 0.0025; // slightly different for width
        this._chorusLfoNode.connect(chorusLfoGainL); chorusLfoGainL.connect(this._chorusDelayL.delayTime);
        this._chorusLfoNode.connect(chorusLfoGainR); chorusLfoGainR.connect(this._chorusDelayR.delayTime);
        this._chorusLfoNode.start();

        // Sidechain duck
        this._duckGain = ac.createGain(); this._duckGain.gain.value = 1.0;

        // === ROUTING ===
        // Instruments → duckGain → filterMain → bitcrusher → compressor
        this._duckGain.connect(this.filterMain);
        this.filterMain.connect(this.bitcrusherShaper);
        this.bitcrusherShaper.connect(this.compressor);

        // Compressor → dry, reverb, delay, chorus, overdrive sends
        this.compressor.connect(reverbDry);
        this.compressor.connect(this.reverbNode); this.reverbNode.connect(reverbWet);
        this.compressor.connect(this.delayL);
        this.compressor.connect(this._chorusDelayL); this._chorusDelayL.connect(this._chorusWet);
        this.compressor.connect(this._chorusDelayR); this._chorusDelayR.connect(this._chorusWet);
        this.compressor.connect(this._overdrive); this._overdrive.connect(this._overdriveGain);
        this._overdriveGain.connect(this._overdriveWet);

        // All sends → masterGain
        reverbDry.connect(this.masterGain);
        reverbWet.connect(this.masterGain);
        this.delayWet.connect(this.masterGain);
        this._chorusWet.connect(this.masterGain);
        this._overdriveWet.connect(this.masterGain);
    }

    _makeBitcrushCurve(bits) {
        const steps = Math.pow(2, bits); const len = 4096; const curve = new Float32Array(len);
        for (let i = 0; i < len; i++) { const x = (i / (len - 1)) * 2 - 1; curve[i] = Math.round(x * steps) / steps; }
        return curve;
    }

    _makeOverdriveCurve(amount) {
        // Soft-clip distortion with adjustable drive
        const len = 8192; const curve = new Float32Array(len);
        const k = amount * 50 + 1;
        for (let i = 0; i < len; i++) {
            const x = (i / (len - 1)) * 2 - 1;
            curve[i] = (Math.PI + k) * x / (Math.PI + k * Math.abs(x));
        }
        return curve;
    }

    _createPlateReverb() {
        const ac = this.audioContext; const sr = ac.sampleRate;
        const length = sr * (0.8 + this.rng() * 0.7);
        const impulse = ac.createBuffer(2, length, sr);
        for (let ch = 0; ch < 2; ch++) {
            const data = impulse.getChannelData(ch);
            for (let i = 0; i < length; i++) {
                const t = i / length;
                data[i] = ((this.rng() * 2 - 1) * Math.pow(1 - t, 1.5 + this.rng() * 0.5) + (t < 0.05 ? 0.8 : 0) * (this.rng() * 2 - 1)) * 0.25;
            }
        }
        this.reverbNode.buffer = impulse;
    }

    // ============================================================
    // VOICE POOL (unchanged)
    // ============================================================
    _trackVoice(osc, env, endTime) {
        this._voices.push({ osc, env, endTime }); this.oscillators.push(osc); this.envelopes.push(env);
        const now = this.audioContext.currentTime;
        this._voices = this._voices.filter(v => {
            if (v.endTime < now) {
                const oi = this.oscillators.indexOf(v.osc); if (oi > -1) this.oscillators.splice(oi, 1);
                const ei = this.envelopes.indexOf(v.env); if (ei > -1) this.envelopes.splice(ei, 1);
                try { v.osc.disconnect(); } catch {}
                try { v.env.disconnect(); } catch {}
                return false;
            }
            return true;
        });
        while (this._voices.length > this._maxVoices) {
            const victim = this._voices.shift();
            try { victim.env.gain.cancelScheduledValues(now); victim.env.gain.setValueAtTime(0, now); victim.osc.stop(now + 0.01); } catch {}
            const oi = this.oscillators.indexOf(victim.osc); if (oi > -1) this.oscillators.splice(oi, 1);
            const ei = this.envelopes.indexOf(victim.env); if (ei > -1) this.envelopes.splice(ei, 1);
            try { victim.osc.disconnect(); } catch {}
            try { victim.env.disconnect(); } catch {}
        }
    }

    // ============================================================
    // NOTE HELPERS
    // ============================================================
    noteToFrequency(scaleDegree, octave = 4) {
        const scale = this.currentScale; const len = scale.length;
        let deg = ((scaleDegree % len) + len) % len;
        let octaveShift = Math.floor(scaleDegree / len);
        if (scaleDegree < 0 && scaleDegree % len !== 0) octaveShift--;
        const semitone = scale[deg] + this.currentKey + (octave + octaveShift - 4) * 12;
        return 440 * Math.pow(2, this._clampSemitone(semitone) / 12);
    }

    _clampSemitone(semitone) {
        const range = this.config.pitchRange || { minSemitone: -24, maxSemitone: 36 };
        let s = semitone;
        while (s < range.minSemitone) s += 12;
        while (s > range.maxSemitone) s -= 12;
        return s;
    }

    _safeTime(t) { return Math.max(t, this.audioContext.currentTime + 0.005); }

    _swingTime(t, stepLen, pos) {
        if (this._swing <= 0 || pos % 2 === 0) return t;
        return t + stepLen * this._swing;
    }

    // ============================================================
    // SYNTH INSTRUMENTS (unchanged from v2)
    // ============================================================
    _createVoice(type, frequency, filterFreq, filterQ) {
        const ac = this.audioContext; const osc = ac.createOscillator(); osc.type = type; osc.frequency.value = frequency;
        const env = ac.createGain(); env.gain.value = 0;
        if (filterFreq) {
            const flt = ac.createBiquadFilter(); flt.type = 'lowpass'; flt.frequency.value = filterFreq; flt.Q.value = filterQ || 1;
            osc.connect(flt); flt.connect(env); return { osc, env, filter: flt };
        }
        osc.connect(env); return { osc, env, filter: null };
    }

    _playVoice(voice, startTime, duration, volume, attack, decay, sustain, release, destination) {
        const t0 = this._safeTime(startTime); const dur = Math.max(duration, 0.05); const dest = destination || this.filterMain;
        const atkEnd = t0 + Math.max(attack, 0.001); const dcyEnd = atkEnd + Math.max(decay, 0.001);
        const susLvl = Math.max(0, Math.min(1, sustain)) * volume;
        const susTime = Math.max(dur - attack - decay - release, 0.005);
        const relStart = t0 + attack + decay + susTime; const endTime = relStart + Math.max(release, 0.001);
        try {
            voice.env.gain.cancelScheduledValues(t0); voice.env.gain.setValueAtTime(0, t0);
            voice.env.gain.linearRampToValueAtTime(volume, atkEnd); voice.env.gain.linearRampToValueAtTime(susLvl, dcyEnd);
            voice.env.gain.setValueAtTime(susLvl, relStart); voice.env.gain.linearRampToValueAtTime(0, endTime);
            voice.env.connect(dest); voice.osc.start(t0); voice.osc.stop(endTime + 0.05);
        } catch { return; }
        this._trackVoice(voice.osc, voice.env, endTime + 0.05);
    }

    _playPulseLead(scaleDegree, octave, startTime, duration, volume) {
        const ls = this.layerStates.get('lead'); if (!ls || !ls.active || ls.intensity < 0.05) return;
        const vol = volume * ls.intensity * this.moodIntensity;
        const freq = this.noteToFrequency(scaleDegree, octave); const ac = this.audioContext; const t0 = this._safeTime(startTime);

        // Primary voice (square)
        const voice = this._createVoice('square', freq, 3500, 1.5);
        this._playVoice(voice, t0, duration, vol * 0.7, 0.01, 0.08, 0.6, 0.12, this.filterMain);

        // Detuned second oscillator (sawtooth, slightly sharp) for PWM-like thickness
        const voice2 = this._createVoice('sawtooth', freq * 1.003, 2800, 1.2);
        this._playVoice(voice2, t0, duration, vol * 0.3, 0.015, 0.1, 0.5, 0.15, this.filterMain);

        // Portamento: glide from previous frequency
        if (this._prevLeadFreq > 0 && Math.abs(freq - this._prevLeadFreq) > 10) {
            try {
                voice.osc.frequency.setValueAtTime(this._prevLeadFreq, t0);
                voice.osc.frequency.exponentialRampToValueAtTime(freq, t0 + 0.025);
                voice2.osc.frequency.setValueAtTime(this._prevLeadFreq * 1.003, t0);
                voice2.osc.frequency.exponentialRampToValueAtTime(freq * 1.003, t0 + 0.03);
            } catch {}
        }
        this._prevLeadFreq = freq;

        // Vibrato with delayed onset
        try {
            const lfo = ac.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = 5.2;
            const lg = ac.createGain(); lg.gain.value = 0; lfo.connect(lg);
            lg.connect(voice.osc.frequency); lg.connect(voice2.osc.frequency);
            lfo.start(t0); lg.gain.setValueAtTime(0, t0); lg.gain.linearRampToValueAtTime(freq * 0.007, t0 + 0.15);
            lfo.stop(t0 + duration + 0.2);
        } catch {}
    }

    _playCountermelody(scaleDegree, octave, startTime, duration, volume) {
        const ls = this.layerStates.get('counter'); if (!ls || !ls.active || ls.intensity < 0.05) return;
        const vol = volume * ls.intensity * this.moodIntensity * 0.4;
        const voice = this._createVoice('triangle', this.noteToFrequency(scaleDegree, octave), 2800, 1.0);
        this._playVoice(voice, startTime, duration, vol, 0.02, 0.1, 0.5, 0.15, this.filterMain);
    }

    _playChipBass(scaleDegree, octave, startTime, duration, volume) {
        const ls = this.layerStates.get('bass'); if (!ls || !ls.active || ls.intensity < 0.05) return;
        const vol = volume * ls.intensity * this.moodIntensity;
        const freq = this.noteToFrequency(scaleDegree, octave); const t0 = this._safeTime(startTime);
        const secDef = this._currentSectionDef || this._sectionDefs.verseA;

        // Acid bass mode: 303-style resonant filter sweep on high-energy sections
        const isAcid = secDef.energy > 0.7 && this.moodIntensity > 0.6;
        const filterCutoff = isAcid ? 2400 : 1200;
        const filterRes = isAcid ? 8 : 2;
        const filterDecay = isAcid ? 0.15 : 0.08;

        const voice = this._createVoice('sawtooth', freq, filterCutoff, filterRes);
        if (voice.filter) {
            voice.filter.frequency.setValueAtTime(filterCutoff, t0);
            voice.filter.frequency.exponentialRampToValueAtTime(isAcid ? 120 : 250, t0 + filterDecay);
            // Accent: occasional filter spike
            if (isAcid && this.rng() < 0.3) {
                voice.filter.frequency.setValueAtTime(filterCutoff * 2, t0);
            }
        }
        this._playVoice(voice, t0, duration, vol, 0.005, 0.06, isAcid ? 0.5 : 0.35, 0.08, this.filterMain);
    }

    _playArpNote(scaleDegree, octave, startTime, duration, volume) {
        const ls = this.layerStates.get('arp'); if (!ls || !ls.active || ls.intensity < 0.05) return;
        const vol = volume * ls.intensity * this.moodIntensity * 0.5;
        const type = this.rng() > 0.5 ? 'square' : 'triangle';
        const voice = this._createVoice(type, this.noteToFrequency(scaleDegree, octave), 4000, 1);
        this._playVoice(voice, startTime, duration, vol, 0.003, 0.03, 0.15, 0.04, this.filterMain);
    }

    _playPluck(scaleDegree, octave, startTime, duration, volume) {
        const ls = this.layerStates.get('pluck'); if (!ls || !ls.active || ls.intensity < 0.05) return;
        const vol = volume * ls.intensity * this.moodIntensity * 0.45;
        const voice = this._createVoice('triangle', this.noteToFrequency(scaleDegree, octave), 5000, 2);
        const t0 = this._safeTime(startTime);
        if (voice.filter) { voice.filter.frequency.setValueAtTime(6000, t0); voice.filter.frequency.exponentialRampToValueAtTime(800, t0 + 0.12); }
        this._playVoice(voice, t0, Math.min(duration, 0.3), vol, 0.002, 0.04, 0.1, 0.08, this.filterMain);
    }

    _playSynthPad(chordTones, octave, startTime, duration, volume) {
        const ls = this.layerStates.get('pad'); if (!ls || !ls.active || ls.intensity < 0.05) return;
        const vol = volume * ls.intensity * this.moodIntensity * 0.35;
        const dest = this._duckGain; const ac = this.audioContext; const t0 = this._safeTime(startTime);
        // Choral: stagger entries
        const stagger = this._accompMode === 'choral' ? 0.08 : 0;
        for (let idx = 0; idx < chordTones.length; idx++) {
            const freq = this.noteToFrequency(chordTones[idx], octave);
            const entryTime = t0 + idx * stagger;
            const noteDur = duration - idx * stagger;

            // Primary voice (sawtooth)
            const voice = this._createVoice('sawtooth', freq, 1200, 0.7);
            this._playVoice(voice, entryTime, noteDur, vol * 0.6, 0.4, 0.3, 0.8, 1.2, dest);

            // Detuned second voice (slightly flat, different waveform for ensemble)
            const voice2 = this._createVoice('square', freq * 0.998, 900, 0.5);
            this._playVoice(voice2, entryTime + 0.02, noteDur - 0.02, vol * 0.3, 0.5, 0.3, 0.7, 1.5, dest);

            // Sub-octave body (sine, one octave down)
            if (idx === 0) {
                const voice3 = this._createVoice('sine', freq * 0.5, null, null);
                this._playVoice(voice3, entryTime, noteDur, vol * 0.2, 0.6, 0.2, 0.9, 1.0, dest);
            }

            // Slow filter modulation on pad voices
            try {
                if (voice.filter) {
                    voice.filter.frequency.setValueAtTime(800, entryTime);
                    voice.filter.frequency.linearRampToValueAtTime(1600, entryTime + noteDur * 0.4);
                    voice.filter.frequency.linearRampToValueAtTime(600, entryTime + noteDur * 0.9);
                }
            } catch {}
        }
    }

    _playSubBass(scaleDegree, octave, startTime, duration, volume) {
        const ls = this.layerStates.get('sub'); if (!ls || !ls.active || ls.intensity < 0.05) return;
        const vol = volume * ls.intensity * this.moodIntensity * 0.6;
        const voice = this._createVoice('sine', this.noteToFrequency(scaleDegree, octave), null, null);
        this._playVoice(voice, startTime, duration, vol, 0.04, 0.1, 0.9, 0.4, this._duckGain);
    }

    _playWobble(scaleDegree, octave, startTime, duration, volume) {
        const ls = this.layerStates.get('wobble'); if (!ls || !ls.active || ls.intensity < 0.05) return;
        const vol = volume * ls.intensity * this.moodIntensity * 0.35;
        const freq = this.noteToFrequency(scaleDegree, octave); const ac = this.audioContext;
        const t0 = this._safeTime(startTime);

        // Main wobble voice (sawtooth with high resonance)
        const voice = this._createVoice('sawtooth', freq, 600, 8);
        this._playVoice(voice, t0, duration, vol * 0.7, 0.025, 0.1, 0.7, 0.15, this.filterMain);

        // Second detuned layer for thickness
        const voice2 = this._createVoice('square', freq * 0.998, 500, 6);
        this._playVoice(voice2, t0, duration, vol * 0.3, 0.03, 0.12, 0.6, 0.18, this.filterMain);

        // Dual-LFO filter modulation: tempo-synced primary + slower secondary
        try {
            const tempoHz = this.config.tempo / 60;
            // Primary LFO: tempo-synced for rhythmic wobble
            const lfo1 = ac.createOscillator(); lfo1.type = 'sine'; lfo1.frequency.value = tempoHz * 2;
            const lg1 = ac.createGain(); lg1.gain.value = 1500;
            lfo1.connect(lg1);
            if (voice.filter) lg1.connect(voice.filter.frequency);
            if (voice2.filter) lg1.connect(voice2.filter.frequency);

            // Secondary LFO: slower, adds movement over time
            const lfo2 = ac.createOscillator(); lfo2.type = 'triangle'; lfo2.frequency.value = tempoHz * 0.5;
            const lg2 = ac.createGain(); lg2.gain.value = 600;
            lfo2.connect(lg2);
            if (voice.filter) lg2.connect(voice.filter.frequency);

            lfo1.start(t0); lfo2.start(t0);
            lfo1.stop(t0 + duration + 0.1); lfo2.stop(t0 + duration + 0.1);
        } catch {}
    }

    _playBleep(startTime, volume) {
        const ls = this.layerStates.get('pluck') || this.layerStates.get('lead');
        if (!ls || ls.intensity < 0.05) return;
        const deg = this.currentScale[Math.floor(this.rng() * this.currentScale.length)];
        const voice = this._createVoice(this.rng() > 0.5 ? 'sine' : 'triangle', this.noteToFrequency(deg, 6 + Math.floor(this.rng() * 2)), null, null);
        this._playVoice(voice, startTime, 0.06, volume * 0.25 * this.moodIntensity, 0.002, 0.02, 0.1, 0.03, this.filterMain);
    }

    // ============================================================
    // DRUM SYNTH (unchanged)
    // ============================================================
    _playKick(startTime, volume) {
        const ls = this.layerStates.get('drums'); if (!ls || !ls.active || ls.intensity < 0.05) return;
        const vol = volume * ls.intensity * this.moodIntensity; const ac = this.audioContext; const t0 = this._safeTime(startTime);
        const body = ac.createOscillator(); body.type = 'sine'; body.frequency.setValueAtTime(150, t0); body.frequency.exponentialRampToValueAtTime(50, t0 + 0.05);
        const bEnv = ac.createGain(); bEnv.gain.setValueAtTime(vol * 0.9, t0); bEnv.gain.exponentialRampToValueAtTime(0.001, t0 + 0.15);
        body.connect(bEnv); bEnv.connect(this.filterMain); body.start(t0); body.stop(t0 + 0.2); this._trackVoice(body, bEnv, t0 + 0.2);
        const click = ac.createOscillator(); click.type = 'triangle'; click.frequency.value = 800;
        const cEnv = ac.createGain(); cEnv.gain.setValueAtTime(vol * 0.4, t0); cEnv.gain.exponentialRampToValueAtTime(0.001, t0 + 0.015);
        click.connect(cEnv); cEnv.connect(this.filterMain); click.start(t0); click.stop(t0 + 0.03); this._trackVoice(click, cEnv, t0 + 0.03);
        this._triggerDuck(t0);
    }

    _playSnare(startTime, volume) {
        const ls = this.layerStates.get('drums'); if (!ls || !ls.active || ls.intensity < 0.05) return;
        const vol = volume * ls.intensity * this.moodIntensity; const ac = this.audioContext; const t0 = this._safeTime(startTime);
        const noise = ac.createBufferSource(); noise.buffer = this._noiseBufferLong;
        const nf = ac.createBiquadFilter(); nf.type = 'bandpass'; nf.frequency.value = 3000; nf.Q.value = 1.5;
        const nEnv = ac.createGain(); nEnv.gain.setValueAtTime(vol * 0.7, t0); nEnv.gain.exponentialRampToValueAtTime(0.001, t0 + 0.1);
        noise.connect(nf); nf.connect(nEnv); nEnv.connect(this.filterMain); noise.start(t0); noise.stop(t0 + 0.2); this._trackVoice(noise, nEnv, t0 + 0.2);
        const body = ac.createOscillator(); body.type = 'sine'; body.frequency.value = 200;
        const bEnv = ac.createGain(); bEnv.gain.setValueAtTime(vol * 0.5, t0); bEnv.gain.exponentialRampToValueAtTime(0.001, t0 + 0.04);
        body.connect(bEnv); bEnv.connect(this.filterMain); body.start(t0); body.stop(t0 + 0.1); this._trackVoice(body, bEnv, t0 + 0.1);
    }

    _playHihat(startTime, volume, open = false) {
        const ls = this.layerStates.get('drums'); if (!ls || !ls.active || ls.intensity < 0.05) return;
        const vol = volume * ls.intensity * this.moodIntensity * 0.6; const ac = this.audioContext; const t0 = this._safeTime(startTime);
        const dur = open ? 0.15 : 0.03; const noise = ac.createBufferSource();
        noise.buffer = open ? this._noiseBufferLong : this._noiseBufferShort;
        const hpf = ac.createBiquadFilter(); hpf.type = 'highpass'; hpf.frequency.value = 7000; hpf.Q.value = 1;
        const env = ac.createGain(); env.gain.setValueAtTime(vol, t0); env.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
        noise.connect(hpf); hpf.connect(env); env.connect(this.filterMain); noise.start(t0); noise.stop(t0 + dur + 0.05); this._trackVoice(noise, env, t0 + dur + 0.05);
    }

    _playClap(startTime, volume) {
        const ls = this.layerStates.get('drums'); if (!ls || !ls.active || ls.intensity < 0.05) return;
        const vol = volume * ls.intensity * this.moodIntensity * 0.5; const ac = this.audioContext; const t0 = this._safeTime(startTime);
        for (let i = 0; i < 3; i++) {
            const off = i * 0.008; const noise = ac.createBufferSource(); noise.buffer = this._noiseBufferShort;
            const bpf = ac.createBiquadFilter(); bpf.type = 'bandpass'; bpf.frequency.value = 2000; bpf.Q.value = 2;
            const env = ac.createGain(); env.gain.setValueAtTime(vol * (1 - i * 0.2), t0 + off);
            env.gain.exponentialRampToValueAtTime(0.001, t0 + off + 0.04);
            noise.connect(bpf); bpf.connect(env); env.connect(this.filterMain); noise.start(t0 + off); noise.stop(t0 + off + 0.05); this._trackVoice(noise, env, t0 + off + 0.05);
        }
    }

    _triggerDuck(time) {
        if (!this._duckGain) return; const t = this._safeTime(time);
        this._duckGain.gain.cancelScheduledValues(t); this._duckGain.gain.setValueAtTime(0.3, t);
        this._duckGain.gain.linearRampToValueAtTime(1.0, t + 0.08);
    }

    // ============================================================
    // COMPOSITION ENGINE
    // ============================================================

    scheduleNote() {
        if (this.audioContext.state !== 'running') return;

        this._updateMoodIntensity();
        this._updateLayers();
        this._updateTempo();
        this._checkPendingMood();
        this.checkAutoMoodChange();

        const tempo = Math.max(40, Math.min(200, this.config.tempo));
        const spb = this._stepsPerBar;
        const stepLen = 60 / (tempo * (spb === 12 ? 3 : 4));
        const beatLen = stepLen * (spb === 12 ? 3 : 4);
        const now = this.audioContext.currentTime;

        if (this.nextNoteTime <= now + 0.02) this.nextNoteTime = now + 0.05;
        const t = this.nextNoteTime;

        // Structural position
        const localPos = this.sequencePosition - this._generationStart;
        const barInGen = Math.floor(localPos / spb);
        const posInBar = localPos % spb;

        // Section tracking
        let barsBeforeSec = 0;
        for (let i = 0; i < this._sectionIndex && i < this._sectionSequence.length; i++) {
            barsBeforeSec += (this._sectionDefs[this._sectionSequence[i]] || this._sectionDefs.verseA).bars;
        }
        const barInSection = barInGen - barsBeforeSec;

        // Section advance
        if (this._currentSectionDef && barInSection >= this._currentSectionDef.bars && posInBar === 0) {
            this._sectionIndex++;
            if (this._sectionIndex < this._sectionSequence.length) {
                const nextSecName = this._sectionSequence[this._sectionIndex] || 'verseA';
                const nextSecDef = this._sectionDefs[nextSecName] || this._sectionDefs.verseA;
                this._triggerTransitionEffect();
                // Impact on high-energy section entries (chorus, combat sections)
                if (nextSecDef.energy > 0.7 && nextSecName !== 'outro') {
                    this._playImpact(t);
                }
                this._applySectionOrchestration(this._sectionIndex);
            }
        }

        // Key offset for modulations
        const keyOffset = (barInGen < this._genKeys.length) ? this._genKeys[barInGen] : 0;
        const savedKey = this.currentKey;
        const savedScale = this.currentScale;
        const savedScaleName = this.currentScaleName;
        this.currentKey = (savedKey + keyOffset + 12) % 12;

        // Scale/mode modulation
        if (barInGen < this._genScales.length && this._genScales[barInGen] && this.scales[this._genScales[barInGen]]) {
            this.currentScaleName = this._genScales[barInGen];
            this.currentScale = [...this.scales[this.currentScaleName]];
        }

        // Chord update
        if (posInBar === 0) {
            this.currentChordIndex = barInGen % this.harmonicProgression.length;
            this.updateChord();
        }

        const secDef = this._currentSectionDef || this._sectionDefs.verseA;

        // ---- DRUMS ----
        this._scheduleDrums(t, stepLen, posInBar, barInSection, secDef);

        // ---- BASS ----
        this._scheduleBass(t, stepLen, beatLen, posInBar, barInGen);

        // ---- SUB BASS ----
        if (posInBar === 0) {
            const chordData = barInGen < this._genChords.length ? this._genChords[barInGen] : [0];
            this._playSubBass(chordData[0], 1, t, beatLen * (this._beatsPerBar - 0.2), 0.5);
        }

        // ---- LEAD MELODY ----
        this._scheduleLead(t, stepLen, beatLen, posInBar, barInGen);

        // ---- COUNTERMELODY ----
        this._scheduleCounter(t, stepLen, beatLen, posInBar, barInGen);

        // ---- ARP ----
        this._scheduleArp(t, stepLen, posInBar, secDef);

        // ---- PAD ----
        const phrasePos = localPos % (spb * 4);
        if (phrasePos === 0) {
            this._playSynthPad(this.currentChord.slice(0, 3), 3, t, beatLen * (this._beatsPerBar * 4 - 0.5), 0.5);
        }

        // ---- WOBBLE ----
        if (posInBar === 0 && secDef.wobbleActive && barInSection % 2 === 1) {
            this._playWobble(this.currentChord[0], 3, t, beatLen * (this._beatsPerBar - 0.5), 0.5);
        }

        // ---- PLUCK ----
        if (secDef.energy > 0.4 && posInBar % 4 === 2 && this.rng() < 0.35) {
            const deg = this.currentChord[Math.floor(this.rng() * this.currentChord.length)];
            this._playPluck(deg, 5, t, stepLen * 2, 0.4);
        }

        // ---- BLEEPS ----
        if (this.rng() > 0.95) this._playBleep(t, 0.5);

        // ---- EVOLVING EFFECTS ----
        this._filterLfoPhase += 0.04;
        this._reverbEvolvePhase += 0.008;
        const st = this._safeTime(t);

        // Techno-style filter modulation: multi-LFO with resonance movement
        const lfo1 = Math.sin(this._filterLfoPhase) * 0.5 + 0.5;         // slow sine
        const lfo2 = Math.sin(this._filterLfoPhase * 2.7) * 0.3;         // faster modulator
        const lfo3 = Math.sin(this._filterLfoPhase * 0.13) * 0.5 + 0.5;  // very slow drift
        const energyCutoff = secDef.energy * 3000 + this.moodIntensity * 2500;
        const filterTarget = 2000 + lfo1 * 3000 + lfo2 * 1500 + energyCutoff + lfo3 * 1000;
        this.filterMain.frequency.setTargetAtTime(Math.max(600, Math.min(14000, filterTarget)), st, 0.12);

        // Resonance (Q) modulation: subtle movement for analog feel
        const qBase = 0.7 + secDef.tension * 2;
        const qMod = Math.sin(this._filterLfoPhase * 0.7) * secDef.tension * 1.5;
        this.filterMain.Q.setTargetAtTime(Math.max(0.5, Math.min(8, qBase + qMod)), st, 0.3);

        // Evolving reverb: wet amount drifts slowly with section context
        if (this._reverbWet && this._currentScene) {
            const reverbBase = this._currentScene.reverbMix;
            const reverbDrift = Math.sin(this._reverbEvolvePhase) * 0.08;
            const secBoost = (secDef.energy < 0.4) ? 0.06 : -0.02; // more reverb in calm sections
            this._reverbWet.gain.setTargetAtTime(
                Math.max(0.05, Math.min(0.5, reverbBase + reverbDrift + secBoost)), st, 0.8
            );
        }

        // Delay feedback evolution: more feedback buildup before section changes
        if (this.delayFeedbackL) {
            const fbBase = 0.25;
            const fbBoost = (barInSection >= (secDef.bars || 8) - 2) ? 0.15 : 0; // pre-transition buildup
            const fbDrift = Math.sin(this._reverbEvolvePhase * 1.3) * 0.05;
            this.delayFeedbackL.gain.setTargetAtTime(Math.min(0.5, fbBase + fbBoost + fbDrift), st, 0.5);
            this.delayFeedbackR.gain.setTargetAtTime(Math.min(0.5, fbBase + fbBoost + fbDrift * 0.8), st, 0.5);
        }

        // Overdrive modulation: follows energy/tension
        if (this._overdriveWet) {
            const odTarget = Math.max(0, (secDef.energy - 0.6) * 0.3 * this.moodIntensity);
            this._overdriveWet.gain.setTargetAtTime(odTarget, st, 0.6);
        }

        // Chorus depth modulation: more in calm sections, less in intense
        if (this._chorusWet) {
            const chorusTarget = 0.1 + (1 - secDef.energy) * 0.15 + Math.sin(this._reverbEvolvePhase * 0.6) * 0.04;
            this._chorusWet.gain.setTargetAtTime(Math.max(0.05, Math.min(0.3, chorusTarget)), st, 0.5);
        }

        // ---- DELAY SYNC ----
        if (posInBar === 0 && barInSection === 0) {
            const bl = 60 / tempo;
            this.delayL.delayTime.setTargetAtTime(bl * 0.75, st, 0.5);
            this.delayR.delayTime.setTargetAtTime(bl * 0.5, st, 0.5);
        }

        // Restore key and scale
        this.currentKey = savedKey;
        this.currentScale = savedScale;
        this.currentScaleName = savedScaleName;

        // Generation cycle
        if (localPos > 0 && localPos >= this._generationLength && posInBar === 0) {
            this._performModulation();
            this._initGeneration();
        }

        this.nextNoteTime += stepLen;
        this.sequencePosition++;
    }

    _scheduleDrums(t, stepLen, posInBar, barInSection, secDef) {
        const kit = this._activeDrumKit;
        if (!kit) return;

        const isFillBar = barInSection === this._drumFillBar;
        const swungT = this._swingTime(t, stepLen, posInBar);

        // On fill bar, overlay the short fill on last 4 steps while keeping the groove on the rest
        if (isFillBar && this._currentFill && posInBar >= this._stepsPerBar - 4) {
            const fillPos = posInBar - (this._stepsPerBar - 4);
            if (this._currentFill.kick[posInBar] || 0) this._playKick(swungT, 0.7);
            if (this._currentFill.snare[posInBar] || 0) this._playSnare(swungT, 0.6);
            if (this._currentFill.hihat && this._currentFill.hihat[posInBar]) this._playHihat(swungT, 0.4, false);
            return;
        }

        const patLen = kit.kick.hits.length;
        const pos = posInBar % patLen;

        if (kit.kick.hits[pos]) this._playKick(swungT, (kit.kick.vel[pos] || 0.7) * 0.85);
        if (kit.snare.hits[pos]) this._playSnare(swungT, (kit.snare.vel[pos] || 0.6) * 0.75);
        if (kit.hihat.hits[pos]) this._playHihat(swungT, (kit.hihat.vel[pos] || 0.5) * 0.6, false);
        if (kit.openHat && kit.openHat.hits[pos]) this._playHihat(swungT, (kit.openHat.vel[pos] || 0.4) * 0.55, true);
        if (kit.clap && kit.clap.hits[pos]) this._playClap(swungT, (kit.clap.vel[pos] || 0.5) * 0.6);
    }

    _scheduleBass(t, stepLen, beatLen, posInBar, barInGen) {
        if (barInGen >= this._genBass.length) return;
        const barData = this._genBass[barInGen];
        if (!barData) return;
        const note = barData[posInBar];
        if (!note) return;
        const swungT = this._swingTime(t, stepLen, posInBar);
        let dur = stepLen * 1.8;
        for (let i = posInBar + 1; i < this._stepsPerBar; i++) { if (barData[i]) { dur = (i - posInBar) * stepLen * 0.9; break; } }
        this._playChipBass(note.deg, note.oct, swungT, dur, 0.7 * note.vel);
    }

    _scheduleLead(t, stepLen, beatLen, posInBar, barInGen) {
        if (barInGen >= this._genMelody.length) return;
        const phrase = this._genMelody[barInGen];
        if (!phrase || !phrase.rhythm[posInBar]) return;
        let noteIdx = 0;
        for (let i = 0; i < posInBar; i++) if (phrase.rhythm[i]) noteIdx++;
        if (noteIdx >= phrase.pitches.length) return;
        const note = phrase.pitches[noteIdx];
        const swungT = this._swingTime(t, stepLen, posInBar);
        let dur = stepLen * 1.5;
        for (let i = posInBar + 1; i < this._stepsPerBar; i++) { if (phrase.rhythm[i]) { dur = (i - posInBar) * stepLen * 0.85; break; } }
        this._playPulseLead(note.deg, this._melodyOctave, swungT, dur, 0.55 * note.vel);
    }

    _scheduleCounter(t, stepLen, beatLen, posInBar, barInGen) {
        if (barInGen >= this._genCounter.length) return;
        const phrase = this._genCounter[barInGen];
        if (!phrase || !phrase.rhythm[posInBar]) return;
        let noteIdx = 0;
        for (let i = 0; i < posInBar; i++) if (phrase.rhythm[i]) noteIdx++;
        if (noteIdx >= phrase.pitches.length) return;
        const note = phrase.pitches[noteIdx];
        let dur = stepLen * 1.8;
        for (let i = posInBar + 1; i < this._stepsPerBar; i++) { if (phrase.rhythm[i]) { dur = (i - posInBar) * stepLen * 0.85; break; } }
        this._playCountermelody(note.deg, this._melodyOctave + 1, t, dur, 0.4 * note.vel);
    }

    _scheduleArp(t, stepLen, posInBar, secDef) {
        if (!secDef.arpActive) return;
        const ls = this.layerStates.get('arp'); if (!ls || !ls.active) return;
        const isArpBeat = secDef.energy > 0.7 ? true : (posInBar % 2 === 0);
        if (!isArpBeat) return;
        const arpNotes = [...this.currentChord]; if (arpNotes.length < 3) arpNotes.push(arpNotes[0] + 7);
        const fn = this._arpStyles[this._arpStyle] || this._arpStyles.ascending;
        const deg = fn(arpNotes, this.arpIndex); this.arpIndex++;
        this._playArpNote(deg, 5, this._swingTime(t, stepLen, posInBar), stepLen * 0.7, 0.5);
    }

    // ============================================================
    // TRANSITIONS
    // ============================================================

    _triggerTransitionEffect() {
        if (!this.audioContext || !this.filterMain) return;
        const ac = this.audioContext; const now = ac.currentTime;
        const secName = this._sectionSequence[this._sectionIndex] || 'verseA';
        const secDef = this._currentSectionDef || this._sectionDefs.verseA;

        // Resonant filter sweep (techno-style: dip then rise with resonance spike)
        const currentFreq = this.filterMain.frequency.value;
        this.filterMain.frequency.cancelScheduledValues(now);
        this.filterMain.frequency.setValueAtTime(currentFreq, now);
        this.filterMain.Q.cancelScheduledValues(now);
        this.filterMain.Q.setValueAtTime(this.filterMain.Q.value, now);

        if (secDef.energy > 0.6) {
            // High-energy transition: gentle dip then open, subtle resonance bump
            this.filterMain.Q.linearRampToValueAtTime(2.5, now + 0.5);
            this.filterMain.frequency.setTargetAtTime(3000, now, 0.4);
            this.filterMain.frequency.setTargetAtTime(9000, now + 2.0, 0.6);
            this.filterMain.Q.setTargetAtTime(0.7, now + 2.5, 0.5);
        } else {
            // Low-energy transition: slow gentle sweep
            this.filterMain.frequency.setTargetAtTime(4000, now, 0.5);
            this.filterMain.frequency.setTargetAtTime(8000, now + 2.5, 0.8);
        }

        // Reverb and delay wash on transitions
        if (this._reverbWet && this._currentScene) {
            const mixMod = secName === 'bridge' ? 1.4 : (secName === 'outro' ? 1.6 : 1.0);
            this._reverbWet.gain.setTargetAtTime(this._currentScene.reverbMix * mixMod, now, 0.5);
            this.delayWet.gain.setTargetAtTime(this._currentScene.delayMix * mixMod, now, 0.5);
            // Gentle feedback swell for tail (ramps up slowly, not a jump)
            if (this.delayFeedbackL) {
                this.delayFeedbackL.gain.setTargetAtTime(0.35, now, 0.6);
                this.delayFeedbackL.gain.setTargetAtTime(0.25, now + 3.0, 0.8);
                this.delayFeedbackR.gain.setTargetAtTime(0.35, now, 0.6);
                this.delayFeedbackR.gain.setTargetAtTime(0.25, now + 3.0, 0.8);
            }
        }

        // Overdrive surge on high-energy transitions
        if (this._overdriveWet && secDef.energy > 0.75) {
            this._overdriveWet.gain.setValueAtTime(0.2, now);
            this._overdriveWet.gain.setTargetAtTime(0, now + 2, 0.5);
        }

        // Noise riser only on dramatic transitions (into chorus or bridge)
        if (secName === 'chorus' || secName === 'bridge') {
            this._playRiser(now, secName === 'chorus' ? 'up' : 'down', 4.0);
        }
    }

    _playRiser(startTime, direction, duration) {
        const ac = this.audioContext; if (!ac) return;
        try {
            const noise = ac.createBufferSource(); noise.buffer = this._noiseBufferLong;
            noise.loop = true;
            const filter = ac.createBiquadFilter();
            filter.type = 'lowpass';
            filter.Q.value = 1.5;
            const env = ac.createGain(); env.gain.value = 0;
            noise.connect(filter); filter.connect(env); env.connect(this.filterMain);

            const t0 = this._safeTime(startTime);
            noise.start(t0);

            if (direction === 'up') {
                // Slow rising wash: barely audible, blends with reverb tail
                filter.frequency.setValueAtTime(300, t0);
                filter.frequency.exponentialRampToValueAtTime(4000, t0 + duration * 0.85);
                filter.frequency.setTargetAtTime(1000, t0 + duration * 0.85, 0.3);
                env.gain.setValueAtTime(0, t0);
                env.gain.linearRampToValueAtTime(0.04, t0 + duration * 0.7);
                env.gain.linearRampToValueAtTime(0, t0 + duration);
            } else {
                // Gentle falling wash
                filter.frequency.setValueAtTime(3000, t0);
                filter.frequency.exponentialRampToValueAtTime(200, t0 + duration);
                env.gain.setValueAtTime(0, t0);
                env.gain.linearRampToValueAtTime(0.035, t0 + duration * 0.2);
                env.gain.linearRampToValueAtTime(0, t0 + duration);
            }

            noise.stop(t0 + duration + 0.05);
            this._trackVoice(noise, env, t0 + duration + 0.05);
        } catch {}
    }

    _playImpact(startTime) {
        const ac = this.audioContext; if (!ac) return;
        try {
            // Low boom impact
            const osc = ac.createOscillator(); osc.type = 'sine';
            const t0 = this._safeTime(startTime);
            osc.frequency.setValueAtTime(80, t0); osc.frequency.exponentialRampToValueAtTime(30, t0 + 0.3);
            const env = ac.createGain(); env.gain.setValueAtTime(0.4, t0);
            env.gain.exponentialRampToValueAtTime(0.001, t0 + 0.5);
            osc.connect(env); env.connect(this.filterMain);
            osc.start(t0); osc.stop(t0 + 0.6);
            this._trackVoice(osc, env, t0 + 0.6);

            // Noise burst layer
            const noise = ac.createBufferSource(); noise.buffer = this._noiseBufferLong;
            const nf = ac.createBiquadFilter(); nf.type = 'lowpass'; nf.frequency.value = 400; nf.Q.value = 1;
            const nEnv = ac.createGain(); nEnv.gain.setValueAtTime(0.2, t0);
            nEnv.gain.exponentialRampToValueAtTime(0.001, t0 + 0.3);
            noise.connect(nf); nf.connect(nEnv); nEnv.connect(this.filterMain);
            noise.start(t0); noise.stop(t0 + 0.3); this._trackVoice(noise, nEnv, t0 + 0.3);
        } catch {}
    }

    // ============================================================
    // MOOD & TRANSITIONS (deferred)
    // ============================================================

    setAutoMoodMode(enabled) {
        this.autoMoodMode = !!enabled;
        if (enabled) this.nextMoodChange = this.sequencePosition + 256 + Math.floor(this.rng() * 512);
    }

    setMood(mood) {
        if (!this.moods[mood]) return;
        // Already the current mood and nothing pending - nothing to do
        if (mood === this.currentMood && !this._pendingMood) return;
        // New pending mood: reset the "sustained since" timer
        if (mood !== this._pendingMood) {
            this._pendingMoodSince = this.sequencePosition;
        }
        this._pendingMood = mood;
    }

    _checkPendingMood() {
        if (!this._pendingMood) return;

        // Apply at bar boundaries (every bar, not every 4 bars)
        const localPos = this.sequencePosition - this._generationStart;
        if (localPos % this._stepsPerBar !== 0) return;

        const mood = this._pendingMood;

        // If already this mood, just clear pending
        if (mood === this.currentMood) { this._pendingMood = null; return; }

        const moodDef = this.moods[mood];

        // === LIGHTWEIGHT (always, at every bar boundary) ===
        // These all crossfade gradually so they're safe to apply immediately
        this.currentMood = mood;
        this._pendingMood = null;
        this.targetIntensity = moodDef.intensity;

        // Update layer targets (actual crossfade is handled by _updateLayers at 0.015/step)
        const moodLayers = moodDef.layers;
        const allLayers = ['lead', 'bass', 'arp', 'pad', 'drums', 'pluck', 'sub', 'wobble', 'counter'];
        for (const layer of allLayers) {
            const st = this.layerStates.get(layer);
            if (!st) continue;
            st.targetIntensity = (moodLayers.includes(layer) || layer === 'drums')
                ? moodDef.intensity * (0.4 + this.rng() * 0.5) : 0;
        }

        // === HEAVYWEIGHT (only if mood sustained for 2+ bars, or dramatic transition) ===
        const sustained = this.sequencePosition - this._pendingMoodSince;
        const isDramatic = (mood === 'combat' || mood === 'victory');
        const sustainThreshold = this._stepsPerBar * 2; // 2 bars

        if (sustained >= sustainThreshold || isDramatic) {
            // Scene change: new tempo, swing, reverb/delay targets
            this._currentScene = moodDef.scenes[Math.floor(this.rng() * moodDef.scenes.length)];
            this._baseTempo = this._currentScene.baseTempo + Math.floor(this.rng() * 8) - 4;
            this.targetTempo = this._baseTempo;
            this._swing = this._currentScene.swing;

            // Drum kit change
            const kitNames = moodDef.drumKits;
            if (this._meter === '4/4') {
                this._activeDrumKit = this._drumKits[kitNames[Math.floor(this.rng() * kitNames.length)]] || this._drumKits.groove;
            }

            // Reverb/delay adapt to new scene
            if (this._reverbWet && this._currentScene) {
                const now = this.audioContext.currentTime;
                this._reverbWet.gain.setTargetAtTime(this._currentScene.reverbMix, now, 0.8);
                this.delayWet.gain.setTargetAtTime(this._currentScene.delayMix, now, 0.8);
            }
        }
    }

    checkAutoMoodChange() {
        if (!this.autoMoodMode || this.sequencePosition < this.nextMoodChange) return;
        const transitions = {
            explore: () => this.rng() > 0.5 ? 'adventure' : 'chill',
            adventure: () => this.rng() > 0.6 ? 'explore' : 'combat',
            combat: () => this.rng() > 0.4 ? 'adventure' : 'explore',
            chill: () => this.rng() > 0.5 ? 'explore' : 'adventure',
            victory: () => 'explore'
        };
        const next = (transitions[this.currentMood] || transitions.explore)();
        this.setMood(next);
        this.nextMoodChange = this.sequencePosition + 256 + Math.floor(this.rng() * 512);
    }

    _updateMoodIntensity() {
        this.moodIntensity += (this.targetIntensity - this.moodIntensity) * 0.02;
        this.moodIntensity = Math.max(0.1, Math.min(1.0, this.moodIntensity));
    }

    _updateLayers() {
        this.layerStates.forEach(st => {
            st.intensity += (st.targetIntensity - st.intensity) * 0.015; // slow crossfade
            st.intensity = Math.max(0, Math.min(1, st.intensity));
            st.active = st.intensity > 0.03;
        });
    }

    _updateTempo() {
        if (Math.abs(this.config.tempo - this.targetTempo) > 0.5) {
            this.config.tempo += Math.sign(this.targetTempo - this.config.tempo) * this.tempoRampRate;
            this.config.tempo = Math.max(40, Math.min(200, this.config.tempo));
        }
    }

    _performModulation() {
        const shifts = [5, 7, 2, -2, 4, -3];
        this.currentKey = (this.currentKey + shifts[Math.floor(this.rng() * shifts.length)] + 12) % 12;
        if (this.rng() > 0.5) {
            const scene = this._currentScene;
            const names = scene ? scene.scales : ['major'];
            this.currentScaleName = names[Math.floor(this.rng() * names.length)];
            this.currentScale = [...this.scales[this.currentScaleName]];
        }
    }

    // ============================================================
    // TRANSPORT
    // ============================================================

    scheduler() {
        if (!this.isPlaying) return;
        if (this.audioContext.state === 'suspended') { this.timerID = setTimeout(() => this.scheduler(), 200); return; }
        try { while (this.nextNoteTime < this.audioContext.currentTime + this.scheduleAheadTime) this.scheduleNote(); }
        catch { /* ignore */ }
        this.timerID = setTimeout(() => this.scheduler(), this.lookahead);
    }

    async start() {
        try {
            if (this.audioContext.state === 'suspended') await this.audioContext.resume();
            if (!this.isPlaying) {
                this.isPlaying = true;
                this.nextNoteTime = this.audioContext.currentTime + 0.2;
                this.sequencePosition = 0; this._generationStart = 0;
                this._initGeneration();
                this.scheduler();
            }
        } catch {}
    }

    stop() {
        this.isPlaying = false;
        if (this.timerID) { clearTimeout(this.timerID); this.timerID = 0; }
        this._voices.forEach(v => { try { v.osc.stop(); v.osc.disconnect(); } catch {} try { v.env.disconnect(); } catch {} }); this._voices = [];
        this.oscillators.forEach(o => { try { o.stop(); o.disconnect(); } catch {} }); this.oscillators = [];
        this.envelopes.forEach(e => { try { e.disconnect(); } catch {} }); this.envelopes = [];
        // Stop chorus LFO
        try { if (this._chorusLfoNode) this._chorusLfoNode.stop(); } catch {}
        this._chorusLfoNode = null;
        if (this._visibilityBound) { document.removeEventListener('visibilitychange', this._visibilityBound); this._visibilityBound = null; }
    }

    setVolume(volume) {
        this.config.volume = Math.max(0, Math.min(1, volume));
        if (this.masterGain) this.masterGain.gain.setTargetAtTime(this.config.volume, this.audioContext.currentTime, 0.1);
    }

    setSeed(seed) { this.config.seed = seed || 1; this.setupSeededRandom(this.config.seed); this.initializeMusicalSystem(); }
    setTempo(tempo) { this.targetTempo = Math.max(40, Math.min(200, tempo)); this._baseTempo = this.targetTempo; }
    getVolume() { return this.config.volume; }
    isActive() { return this.isPlaying; }
}
